// Catalog of every operation (Jambonz verb) extracted from the numera-apps-server
// spec. Each verb declares:
//   - category/color   → palette grouping + node colour
//   - terminal         → ends a sequence (no outgoing flow)
//   - handles          → source connection points; role drives compilation:
//       next          → continue the same handler (inline)
//       action        → sets verb.actionHook  to the target handler path
//       wait          → sets verb.waitHook     to the target handler path
//       to            → redirect: sets verb.actionHook to the target path
//       transcription → sets verb.transcriptionHook to the target path
//   - fields           → param editor schema for the config drawer
//   - defaults         → initial node data
//   - build(data,hooks)→ emits the final verb JSON object

import { CONNECTOR_CAPS } from "./capabilities";
import { byType as connByType } from "../Connectors/catalog";

const clean = obj => {
  const out = {};
  Object.keys(obj).forEach(k => {
    const v = obj[k];
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v) && v.length === 0) return;
    out[k] = v;
  });
  return out;
};

const num = v => (v === "" || v === undefined || v === null ? undefined : Number(v));

// speaking-rate -> a style instruction the gpt-4o-mini-tts model follows (the
// jambonz synthesizer has no numeric speed; it has `prompt` = style guidance).
export const rateInstruction = rate =>
  rate === "slow"
    ? "Fale em um ritmo pausado e mais lento."
    : rate === "fast"
    ? "Fale em um ritmo mais rápido e ágil."
    : "";

// IVR background-music volume index (1..5) -> dB gain for the dub track
export const ivrVolumeGain = v => {
  const map = { 1: "-30 dB", 2: "-24 dB", 3: "-18 dB", 4: "-12 dB", 5: "-6 dB" };
  return map[v] || map[3];
};

const jsonOr = (s, fallback) => {
  if (!s || typeof s !== "string") return fallback;
  try {
    return JSON.parse(s);
  } catch (e) {
    return fallback;
  }
};

// Coletar: a pergunta é falada (TTS) ou tocada (MP3). Nós criados ANTES do campo
// não têm promptMode — nesses, ter áudio escolhido significa "Tocar áudio";
// caso contrário "Falar", que era o comportamento único até então.
const promptMode = d => (d && d.promptMode) || (d && d.promptAudio ? "play" : "say");
// Coletar: como a coleta termina. Nós antigos não têm o campo — caem em "pause",
// que era o comportamento único até aqui.
const endMode = d => (d && d.endMode) || "pause";
// Coletar: o nó aceita este tipo de entrada? Serve para esconder o que não tem
// efeito — o ajuste fino da fala numa coleta só de teclas, por exemplo. Espelha
// o fallback do build(): sem o campo, vale "digits".
const aceita = (d, tipo) => {
  const lista = d && Array.isArray(d.input) && d.input.length ? d.input : ["digits"];
  return lista.indexOf(tipo) >= 0;
};

// Nós que só existem DENTRO de uma chamada: reexecutá-los isolados não faz
// sentido — uma pergunta falada precisa de alguém na linha. Usado pelo botão
// "Executar novamente" da aba Teste & resultados. A lista é por EXCLUSÃO de
// propósito: um nó de backend novo já nasce reexecutável, e o backend valida de
// novo de qualquer jeito (flow/registry.ts é a fonte da verdade).
export const VERBOS_DE_CHAMADA = new Set([
  "answer", "say", "play", "pause", "gather", "ivr", "dial", "ring_group",
  "conference", "enqueue", "dequeue", "leave", "hangup", "listen", "llm",
  "transcribe", "dub", "redirect", "tag", "config", "detect_answer", "schedule",
  "transfer_ai", "dtmf", "send_dtmf", "originate",
]);
// nós de controle/terminais: rodam, mas "executar de novo" não produz nada
// observável sozinho (o loop é percorrido pelo walker, o Retornar só devolve).
const SEM_REEXECUCAO = new Set(["loop", "loop_end", "return", "http_response", "route_agent"]);
export const podeReexecutar = verbKey =>
  !!verbKey && !VERBOS_DE_CHAMADA.has(verbKey) && !SEM_REEXECUCAO.has(verbKey);

export const CATEGORIES = [
  { key: "audio", label: "Fala & Áudio", color: "#2C353D" },
  { key: "input", label: "Entrada", color: "#0B7285" },
  { key: "routing", label: "Roteamento", color: "#5F3DC4" },
  { key: "call", label: "Chamada", color: "#1E7E34" },
  { key: "queue", label: "Filas & Conferência", color: "#B26A00" },
  { key: "ai", label: "Inteligência", color: "#A61E4D" },
  { key: "advanced", label: "Avançado", color: "#5F5E5A" },
  { key: "data", label: "Dados", color: "#2B8A3E" },
  { key: "connectors", label: "Conectores", color: "#0B7285" },
];

export const VERBS = {
  // "Retornar" — nó terminal só do modo Ferramenta: define o valor devolvido à
  // IA quando a ferramenta termina (mapeado dos steps.*) + uma descrição.
  return: {
    label: "return",
    title: "Retornar",
    category: "advanced",
    terminal: true,
    toolOnly: true,
    handles: [],
    defaults: { value: "", description: "" },
    fields: [
      { name: "value", label: "Valor a retornar (use {{steps.*}})", type: "textarea" },
      { name: "description", label: "Descrição do retorno (a IA lê)", type: "text" },
    ],
    summary: d => d.value,
    build: d => clean({ verb: "return", value: d.value, _description: d.description || undefined }),
  },
  // "Retornar ao coletor" — nó terminal só do modo Coletor: entrega a LISTA de
  // registros e diz qual campo é o SEQUENCIAL (cursor). O coletor usa esse campo
  // pra trazer só o que é novo (WHERE cursor > último) na próxima rodada.
  collector_return: {
    label: "collector_return",
    title: "Retornar ao coletor",
    category: "advanced",
    terminal: true,
    collectorOnly: true,
    handles: [],
    defaults: { value: "", cursorField: "" },
    fields: [
      { name: "value", label: "Registros a retornar (lista) — use {{steps.*}}", type: "textarea", required: true },
      { name: "cursorField", label: "Campo sequencial (cursor, ex.: id)", type: "text", required: true },
    ],
    summary: d => (d.cursorField ? `cursor: ${d.cursorField}` : d.value),
    build: d => clean({ verb: "collector_return", value: d.value, cursorField: d.cursorField || undefined }),
  },
  say: {
    label: "say",
    title: "Falar",
    ai: "Fala um texto na ligação (voz/TTS). Use para avisos curtos / frase única. Para CONVERSAR (perguntar e entender respostas), prefira 'transfer_ai' (Agente de IA de voz).",
    category: "audio",
    handles: [{ id: "next", label: "" }],
    defaults: { text: "Olá!", loop: 1 },
    fields: [
      { name: "text", label: "Texto", type: "textarea", required: true },
      { name: "voice", label: "Voz", type: "voice" },
      { name: "loop", label: "Repetições", type: "number" },
    ],
    summary: d => d.text,
    build: d =>
      clean({
        verb: "say",
        text: d.text,
        loop: num(d.loop) !== 1 ? num(d.loop) : undefined,
        // voz do nó (interna; herda a voz do projeto se vazia). _ttsVendor/_rate
        // são stripados antes de servir; o backend pré-renderiza no deploy.
        _ttsVendor: d.ttsVendor || undefined,
        synthesizer: d.voice ? { voice: d.voice } : undefined,
        _rate: d.rate && d.rate !== "normal" ? d.rate : undefined,
      }),
  },
  send_dtmf: {
    label: "send_dtmf",
    title: "Enviar DTMF",
    ai: "Envia tons DTMF (teclas) na ligação ativa — para navegar numa URA de terceiro, mandar um ramal/código, etc. Os dígitos aceitam {{variáveis}} (ex.: {{steps.gather.digits}} ou {{session.ramal}}).",
    category: "call",
    handles: [{ id: "next", label: "" }],
    defaults: { digits: "" },
    fields: [
      { name: "digits", label: "Dígitos (aceita {{variáveis}})", type: "text", required: true, placeholder: "1234# ou {{session.ramal}}" },
      { name: "duration", label: "Duração de cada tom (ms, opcional)", type: "number" },
    ],
    summary: d => `DTMF: ${d.digits || "—"}`,
    build: d => clean({ verb: "dtmf", dtmf: d.digits, duration: num(d.duration) || undefined }),
  },
  play: {
    label: "play",
    title: "Tocar áudio",
    ai: "Toca um arquivo de áudio na ligação (gravação, música, jingle).",
    category: "audio",
    handles: [{ id: "next", label: "" }],
    defaults: { url: "", loop: 1 },
    fields: [
      { name: "url", label: "Áudio", type: "audio", required: true },
      { name: "loop", label: "Repetições", type: "number" },
    ],
    summary: d => d.url,
    build: d => clean({ verb: "play", url: d.url, loop: num(d.loop) !== 1 ? num(d.loop) : undefined }),
  },
  pause: {
    label: "pause",
    title: "Pausa",
    category: "audio",
    handles: [{ id: "next", label: "" }],
    defaults: { length: 2 },
    fields: [{ name: "length", label: "Segundos", type: "number" }],
    summary: d => `${d.length || 0}s`,
    build: d => clean({ verb: "pause", length: num(d.length) }),
  },
  dtmf: {
    label: "dtmf",
    title: "Enviar DTMF",
    category: "audio",
    handles: [{ id: "next", label: "" }],
    defaults: { dtmf: "", duration: 250 },
    fields: [
      { name: "dtmf", label: "Tons (ex: 1234#)", type: "text" },
      { name: "duration", label: "Duração (ms)", type: "number" },
    ],
    summary: d => d.dtmf,
    build: d => clean({ verb: "dtmf", dtmf: d.dtmf, duration: num(d.duration) }),
  },
  dub: {
    label: "dub",
    title: "Faixa de áudio",
    category: "audio",
    handles: [{ id: "next", label: "" }],
    defaults: { track: "background", url: "" },
    fields: [
      { name: "track", label: "Faixa", type: "text" },
      { name: "url", label: "URL", type: "text" },
    ],
    summary: d => d.track,
    build: d =>
      clean({ verb: "dub", action: "addTrack", track: d.track, play: d.url || undefined, loop: !!d.url }),
  },

  gather: {
    label: "gather",
    title: "Coletar (DTMF/fala)",
    ai: "Faz uma pergunta e captura a resposta (dígitos do teclado ou fala). A resposta fica em steps.<nó>; continue pela saída 'action'. Prefira para URA determinística (menu 'toque 1…'); para CONVERSA natural, prefira 'transfer_ai' (Agente de IA de voz).",
    category: "input",
    handles: [{ id: "action", label: "actionHook" }],
    defaults: { promptMode: "say", sayText: "Pressione 1 ou 2.", input: ["digits"], numDigits: 1, timeout: 8, endMode: "pause", silenceMs: 2000, asrSeconds: 3, bargein: false },
    fields: [
      {
        name: "promptMode",
        label: "Antes de escutar",
        type: "toggle",
        default: "say",
        options: [
          { value: "say", label: "Falar" },
          { value: "play", label: "Tocar áudio" },
          { value: "nada", label: "Só escutar" },
        ],
        info: "O que a ligação faz ANTES de começar a escutar. \"Falar\" lê um texto com a voz do projeto; \"Tocar áudio\" toca um MP3. \"Só escutar\" não emite nada e começa a captura na hora — é o certo quando quem fala é o outro lado (uma URA que dita um código, por exemplo): qualquer coisa tocada aqui atrasa a captura e come o começo da frase.",
      },
      // nós antigos não têm promptMode: se já houver áudio escolhido, ele manda —
      // senão cai em "Falar", que era o comportamento único até aqui.
      { name: "sayText", label: "Texto", type: "textarea", showIf: d => promptMode(d) === "say" },
      { name: "promptAudio", label: "Áudio", type: "audio", showIf: d => promptMode(d) === "play" },
      {
        name: "input",
        label: "O que coletar",
        type: "multiselect",
        // os valores continuam digits/speech (é o que o jambonz espera); só o
        // rótulo virou português — "digits"/"speech" na tela não dizia nada.
        options: [
          { value: "digits", label: "Teclas (DTMF)" },
          { value: "speech", label: "Fala" },
        ],
        info: "Marque os dois para aceitar o que vier primeiro. O resultado fica em steps.<nó>.digits (teclas) ou steps.<nó>.transcript (fala).",
      },
      {
        name: "numDigits",
        label: "Nº de teclas esperadas",
        type: "number",
        min: 1,
        // sem "digits" marcado o campo não tem efeito nenhum — some da tela.
        showIf: d => aceita(d, "digits"),
        info: "Encerra sozinho ao receber essa quantidade de teclas, sem esperar o timeout. Deixe vazio se a quantidade varia — aí o encerramento é por tecla final (#) ou por tempo.",
      },
      {
        name: "stopWhen",
        label: "Encerrar assim que reconhecer",
        type: "select",
        showIf: d => aceita(d, "speech"),
        options: [
          { value: "silencio", label: "Não — esperar o silêncio" },
          { value: "digitos", label: "Um número de dígitos falados" },
          { value: "texto", label: "Um texto específico" },
          { value: "regex", label: "Uma expressão (avançado)" },
        ],
        info: "Segue o fluxo assim que a fala já tiver o que você precisa, sem esperar o silêncio nem a finalização do reconhecedor (que leva ~2s). É o que salva o resultado quando o outro lado desliga logo depois de falar — uma URA ditando um código, por exemplo.",
      },
      {
        name: "stopDigits",
        label: "Quantos dígitos",
        type: "number",
        min: 1,
        showIf: d => aceita(d, "speech") && d && d.stopWhen === "digitos",
        info: "Conta os dígitos ditos na fala — inclusive por extenso (\"quatro\" vale 4). Um código de 6 dígitos: 6.",
      },
      {
        name: "stopText",
        label: "Texto que encerra",
        type: "text",
        showIf: d => aceita(d, "speech") && d && d.stopWhen === "texto",
        info: "Trecho que, ao aparecer na fala, encerra a coleta. Não diferencia maiúsculas de minúsculas.",
      },
      {
        name: "stopRegex",
        label: "Expressão regular",
        type: "text",
        placeholder: "\\d{6}",
        showIf: d => aceita(d, "speech") && d && d.stopWhen === "regex",
        info: "Expressão regular avaliada a cada trecho reconhecido. Padrão inválido nunca dispara — a coleta segue normal.",
      },
      {
        name: "endMode",
        label: "Quando encerrar a coleta",
        type: "toggle",
        default: "pause",
        advanced: true,
        showIf: d => aceita(d, "speech"),
        options: [
          { value: "pause", label: "Após uma pausa" },
          { value: "continuous", label: "Quando a outra ponta terminar" },
        ],
        info:
          "\"Após uma pausa\" fecha no primeiro silêncio — bom para respostas curtas. \"Quando a outra ponta terminar\" acumula tudo que for dito e só devolve quando o outro lado realmente para — use para ouvir um anúncio ou um código ditado com pausas.",
      },
      {
        name: "silenceMs",
        label: "Pausa que encerra a fala (ms)",
        type: "number",
        min: 1000,
        advanced: true,
        // o mínimo fica no helperText (sempre visível), não no ícone: abaixo de
        // 1000 o Deepgram recusa a conexão e a CHAMADA CAI — não é curiosidade.
        helperText: "Mínimo 1000.",
        info: "Silêncio, em milissegundos, para considerar a fala terminada. Abaixo de 1000 o reconhecedor recusa a conexão e a chamada cai.",
        showIf: d => aceita(d, "speech") && endMode(d) === "pause",
      },
      {
        name: "asrSeconds",
        label: "Silêncio para considerar que terminou (s)",
        type: "number",
        min: 1,
        advanced: true,
        info: "Segundos sem ninguém falar para fechar a coleta. O relógio reinicia a cada trecho reconhecido.",
        showIf: d => aceita(d, "speech") && endMode(d) === "continuous",
      },
      {
        name: "timeout",
        label: "Esperar resposta por (s)",
        type: "number",
        min: 1,
        advanced: true,
        // No modo contínuo os dois tempos SOMAM: o jambonz só inicia o relógio de
        // silêncio depois que este timeout estoura (gather.js:747), e este
        // reinicia a cada fala. Resultado: a coleta só fecha timeout + silêncio
        // após a outra ponta calar. Com 10s + 1s dá 11s de espera morta.
        helperText: d =>
          aceita(d, "speech") && endMode(d) === "continuous" ? "Soma com o silêncio acima — use 3 a 5." : undefined,
        info:
          "Rede de segurança: quanto esperar até desistir e seguir pela saída do nó sem resposta. Não é o que encerra uma coleta bem-sucedida — quem encerra é o silêncio, o nº de teclas ou a regra de reconhecimento.",
      },
      {
        name: "bargein",
        label: "Permitir interromper a pergunta",
        type: "boolean",
        advanced: true,
        info: "Deixa a outra ponta responder por cima da pergunta, sem esperar terminar. Bom para quem já conhece o menu; ruim se a pergunta precisa ser ouvida inteira.",
      },
    ],
    summary: d =>
      promptMode(d) === "nada"
        ? "só escuta"
        : promptMode(d) === "play"
        ? d.promptAudio
          ? "áudio"
          : ""
        : d.sayText,
    // values this node makes available to later nodes (steps.<node>.<key>)
    exports: [
      { key: "transcript", label: "Texto falado", sample: "texto reconhecido" },
      { key: "digits", label: "Dígitos (DTMF)", sample: "1" },
    ],
    build: (d, h) =>
      clean({
        verb: "gather",
        actionHook: h.action,
        input: d.input && d.input.length ? d.input : ["digits"],
        // campos escondidos não vão para o jambonz: trocar a coleta para só
        // teclas deixaria um ajuste de fala antigo agindo por baixo.
        numDigits: aceita(d, "digits") ? num(d.numDigits) : undefined,
        timeout: num(d.timeout),
        bargein: d.bargein || undefined,
        // um OU outro: play e say juntos no mesmo gather seriam tocados em
        // sequência, o que ninguém espera ao escolher um dos dois. Em
        // "Só escutar" (promptMode "nada") não sai nenhum dos dois — o texto
        // antigo continua guardado no nó, então voltar para "Falar" o recupera.
        play: promptMode(d) === "play" && d.promptAudio ? { url: d.promptAudio } : undefined,
        say: promptMode(d) === "say" && d.sayText ? { text: d.sayText } : undefined,
        // Só emite o recognizer quando o usuário define a pausa; sem isso o
        // backend aplica o padrão global (defaultRecognizer). O que vem daqui
        // VENCE o padrão — ver applyRecognizerToVerb.
        // "pausa"      -> utteranceEndMs: fecha no primeiro silêncio longo o bastante
        // "outra ponta" -> asrTimeout (SEGUNDOS): modo contínuo do jambonz, que
        //   acumula os trechos e só resolve após N s de silêncio — o relógio
        //   reinicia a cada transcrição parcial (gather.js:1132). É o único jeito
        //   de ouvir quem fala em blocos com pausas curtas: nesse caso o
        //   UtteranceEnd do Deepgram quase nunca chega e a coleta ficava presa
        //   até alguém desligar.
        // regra de parada: campo INTERNO (_stopWhen), consumido pelo nosso backend
        // ao servir o gather — não vai para o jambonz. Ver libs/gatherStopWhen.
        _stopWhen:
          aceita(d, "speech") && d.stopWhen && d.stopWhen !== "silencio"
            ? clean({
                mode: d.stopWhen,
                value:
                  d.stopWhen === "digitos" ? String(num(d.stopDigits) || "") :
                  d.stopWhen === "texto" ? d.stopText :
                  d.stopRegex,
              })
            : undefined,
        recognizer: !aceita(d, "speech")
          ? undefined
          : endMode(d) === "continuous"
            ? num(d.asrSeconds)
              ? { asrTimeout: num(d.asrSeconds) }
              : undefined
            : num(d.silenceMs)
            ? { deepgramOptions: { utteranceEndMs: num(d.silenceMs) } }
            : undefined,
      }),
  },

  ivr: {
    label: "ivr",
    title: "Menu URA",
    ai: "Menu de opções por tecla/fala (URA). Cada opção é uma saída — conecte CADA uma ao seu destino.",
    category: "input",
    // dynamic outputs: one per mapped digit + "noinput" (timeout). Rendered like
    // a branch and routed by OUR backend at call time (splitIvr / serveIvr).
    isIvr: true,
    defaults: {
      prompt: "Bem-vindo. Para vendas, digite 1. Para suporte, digite 2.",
      ttsVendor: "openai",
      voice: "",
      rate: "normal",
      digits: ["1", "2"],
      timeout: 8,
      opening: "",
      bgMusic: "",
      bgVolume: 3,
    },
    fields: [
      { name: "menu", label: "Mensagem e voz", type: "ivrMenu" },
      { name: "opening", label: "Áudio de abertura", type: "audio", audioKind: "intro" },
      { name: "bgMusic", label: "Música de fundo", type: "audio", audioKind: "background" },
      {
        name: "bgVolume",
        label: "Volume da música",
        type: "select",
        options: [
          { value: 1, label: "1 — bem baixo" },
          { value: 2, label: "2" },
          { value: 3, label: "3 — médio" },
          { value: 4, label: "4" },
          { value: 5, label: "5 — alto" },
        ],
      },
      { name: "timeout", label: "Tempo p/ digitar (s)", type: "number" },
    ],
    summary: d => d.prompt,
    // outputs + build handled specially (see compile.js compileIvr)
  },

  wa_menu: {
    label: "wa_menu",
    title: "Menu WhatsApp",
    ai: "Menu de WhatsApp: envia uma mensagem com OPÇÕES (botões) e ESPERA a resposta da pessoa. Cada opção é uma SAÍDA — conecte cada uma ao seu destino (pode ser outro Menu). É o jeito de fazer bot conversacional. 'default' = resposta não reconhecida (reenvia o menu).",
    category: "input",
    isWaMenu: true,
    defaults: {
      connectorId: "",
      body: "Como podemos te ajudar? Escolha uma opção:",
      options: ["Opção 1", "Opção 2"],
      final: "",
      ttl: 120,
    },
    fields: [
      { name: "connectorId", label: "Conector WhatsApp", type: "connector", connectorType: "gupshup", required: true },
      { name: "body", label: "Mensagem", type: "textarea", required: true },
      { name: "options", label: "Opções (botões)", type: "stringlist" },
      { name: "ttl", label: "Tempo p/ responder (s) — esquece a conversa se passar", type: "number" },
      { name: "final", label: "Mensagem ao finalizar (opcional)", type: "text" },
    ],
    summary: d => d.body,
    // saídas (uma por opção + default) e build: ver compile.js (compileWaMenu)
  },

  branch: {
    label: "branch",
    title: "Condicional (branch)",
    ai:
      "Ramifica o fluxo comparando UMA variável com uma lista de valores possíveis. " +
      "`field` = a variável a avaliar; `caseKeys` = os valores esperados, UM por caso. " +
      "OS VALORES PODEM SER TEXTO — não são só dígitos. Derive-os do que o nó ANTERIOR " +
      "realmente produz: se a coleta foi por fala, ou se veio de um campo do webhook, de " +
      "uma resposta de IA ou de um status, use as palavras de verdade " +
      '(ex.: caseKeys ["boleto","cartão","pix"], ["aprovado","recusado"], ["sim","não"]). ' +
      'Só use ["1","2"] quando a variável for MESMO um dígito de teclado (DTMF). ' +
      "NÃO deixe o default: informe sempre `caseKeys` ao criar o nó. " +
      "A comparação ignora maiúsculas/minúsculas e espaços nas pontas; o que não casar cai em 'default'. " +
      "Depois conecte CADA saída com connect usando source_handle = o valor exato do caso (ou 'default').",
    category: "routing",
    isBranch: true,
    defaults: { field: "", caseKeys: ["1", "2"] },
    fields: [
      {
        name: "field",
        label: "Avaliar a variável",
        type: "variable",
        placeholder: "ex.: {{steps.coletar.digits}}",
        helperText: "Variável a comparar com os casos abaixo. Escolha pelo ícone.",
      },
    ],
    summary: d => `por: ${d.field || "—"}`,
    // branch is compiled specially (see compile.js)
  },
  redirect: {
    label: "redirect",
    title: "Redirecionar",
    category: "routing",
    handles: [{ id: "to", label: "→ handler" }],
    defaults: {},
    fields: [],
    summary: () => "→ handler",
    build: (d, h) => clean({ verb: "redirect", actionHook: h.to }),
  },
  schedule: {
    label: "schedule",
    title: "Condição de horário",
    ai: "Ramifica conforme o horário/dia (ex.: dentro x fora do horário comercial).",
    category: "routing",
    // two named branches; evaluated by OUR backend at call time (splitSchedule /
    // serveSchedule), like the AMD node. "open" = inside the window, "closed" = outside.
    handles: [
      { id: "open", label: "Dentro" },
      { id: "closed", label: "Fora" },
    ],
    defaults: {
      timezone: "America/Sao_Paulo",
      days: ["mon", "tue", "wed", "thu", "fri"],
      start: "08:00",
      end: "18:00",
    },
    fields: [
      {
        name: "days",
        label: "Dias",
        type: "multiselect",
        options: [
          { value: "mon", label: "Seg" },
          { value: "tue", label: "Ter" },
          { value: "wed", label: "Qua" },
          { value: "thu", label: "Qui" },
          { value: "fri", label: "Sex" },
          { value: "sat", label: "Sáb" },
          { value: "sun", label: "Dom" },
        ],
      },
      { name: "start", label: "Início (HH:MM)", type: "text" },
      { name: "end", label: "Fim (HH:MM)", type: "text", helperText: "Se o Fim for menor que o Início, a janela cruza a meia-noite (ex.: 22:00 → 06:00)." },
      {
        name: "timezone",
        label: "Fuso horário",
        type: "select",
        options: ["America/Sao_Paulo", "America/Manaus", "America/Fortaleza", "America/Cuiaba", "UTC"],
      },
    ],
    summary: d => `${(d.days || []).length} dias · ${d.start || "?"}–${d.end || "?"}`,
    build: (d, h) => ({
      verb: "schedule",
      _schedule: {
        timezone: d.timezone || "America/Sao_Paulo",
        days: d.days && d.days.length ? d.days : ["mon", "tue", "wed", "thu", "fri"],
        start: d.start || "00:00",
        end: d.end || "23:59",
      },
      _scheduleRoutes: clean({ open: h.open, closed: h.closed }),
    }),
  },

  dial: {
    label: "dial",
    title: "Transferir (dial)",
    ai: "Liga/transfere a chamada para um número, ramal SIP ou usuário. Use para encaminhar a pessoa a um atendente.",
    category: "call",
    handles: [{ id: "action", label: "dial-done" }],
    defaults: { targetType: "phone", targetValue: "+5511999999999", answerOnBridge: true, timeout: 30, callerId: "" },
    fields: [
      { name: "targetType", label: "Tipo de destino", type: "select", options: ["phone", "sip", "user"] },
      { name: "targetValue", label: "Destino (número / SIP URI / usuário)", type: "text" },
      { name: "answerOnBridge", label: "answerOnBridge", type: "boolean" },
      { name: "timeout", label: "Timeout (s)", type: "number" },
      { name: "callerId", label: "Caller ID", type: "callerid" },
      { name: "dialMusic", label: "Música em espera", type: "audio", audioKind: "background", helperText: "Toca pro chamador enquanto a transferência conecta (em vez do toque). Vazio = ringback padrão." },
    ],
    summary: d => d.targetValue,
    build: (d, h) => {
      const t =
        d.targetType === "sip"
          ? { type: "sip", sipUri: d.targetValue }
          : d.targetType === "user"
          ? { type: "user", name: d.targetValue }
          : { type: "phone", number: d.targetValue };
      return clean({
        verb: "dial",
        target: [t],
        answerOnBridge: d.answerOnBridge || undefined,
        timeout: num(d.timeout),
        callerId: d.callerId || undefined,
        dialMusic: d.dialMusic || undefined,
        actionHook: h.action,
      });
    },
  },
  ring_group: {
    label: "dial",
    title: "Tocar grupo",
    category: "call",
    // simring: dials all chosen extensions at once; first to answer wins. Two
    // named outputs routed by OUR backend from dial_call_status (serveRingGroup).
    handles: [
      { id: "answered", label: "Atendida" },
      { id: "noanswer", label: "Ninguém atendeu" },
    ],
    defaults: { exts: [], timeout: 25, callerId: "" },
    fields: [
      { name: "exts", label: "Ramais que tocam", type: "extensions" },
      { name: "timeout", label: "Tempo de toque (s)", type: "number" },
      { name: "callerId", label: "Caller ID (opcional)", type: "callerid" },
    ],
    summary: d => `${(d.exts || []).length} ramais · ${d.timeout || 25}s`,
    build: (d, h) => ({
      verb: "dial",
      answerOnBridge: true,
      timeout: num(d.timeout) || 25,
      callerId: d.callerId || undefined,
      target: (d.exts || []).map(u => ({ type: "user", name: u })),
      _ringGroup: true,
      _rgRoutes: clean({ answered: h.answered, noanswer: h.noanswer }),
    }),
  },
  originate: {
    label: "originate",
    title: "Iniciar chamada",
    category: "call",
    handles: [{ id: "next", label: "" }],
    defaults: { to: "", callerId: "" },
    fields: [
      { name: "to", label: "Destino (to)", type: "text" },
      { name: "callerId", label: "Caller ID (from)", type: "callerid" },
      { name: "record", label: "Gravar chamada", type: "boolean", helperText: "Grava o áudio da chamada (armazenado com segurança). Avise o interlocutor — exigido por lei (LGPD)." },
      { name: "recordPublic", label: "Tornar a gravação pública (link)", type: "boolean", showIf: d => !!d.record, helperText: "Gera um link ASSINADO da gravação, acessível pela internet SEM login (para compartilhar). O link expira no tempo abaixo." },
      {
        name: "recordPublicTtl",
        label: "Validade do link público",
        type: "select",
        showIf: d => !!d.record && !!d.recordPublic,
        options: [
          { value: "1h", label: "1 hora" },
          { value: "24h", label: "24 horas" },
          { value: "7d", label: "7 dias" },
          { value: "30d", label: "30 dias" },
        ],
      },
    ],
    summary: d => d.to,
    build: d => clean({ verb: "originate", to: d.to, callerId: d.callerId, record: d.record || undefined, recordPublic: (d.record && d.recordPublic) || undefined, recordPublicTtl: (d.record && d.recordPublic && d.recordPublicTtl) || undefined }),
  },
  detect_answer: {
    label: "amd",
    title: "Detectar atendimento",
    // busca da paleta: quem procura por "caixa postal" tem que achar este nó
    keywords: "caixa postal secretária secretaria eletrônica eletronica voicemail amd máquina maquina humano",
    category: "call",
    // three named branches: who/what answered. Each becomes its own handler,
    // served by OUR backend when jambonz reports the AMD result (see splitAmd /
    // serveAmd). Order matters for the footer layout.
    handles: [
      { id: "human", label: "Humano" },
      { id: "machine", label: "C. postal" },
      { id: "unknown", label: "Indefinido" },
    ],
    defaults: {
      machineTrigger: "detected",
      language: "pt-BR",
      thresholdWordCount: 6,
      noSpeechTimeout: 5,
      decisionTimeout: 15,
    },
    fields: [
      {
        name: "machineTrigger",
        label: 'Seguir "Caixa postal"',
        type: "select",
        options: [
          { value: "detected", label: "Assim que detectar a máquina" },
          { value: "stopped", label: "Após a saudação terminar (deixar recado)" },
        ],
      },
      { name: "language", label: "Idioma (reconhecimento)", type: "select", options: ["pt-BR", "en-US", "es-ES"] },
      { name: "thresholdWordCount", label: "Limite de palavras p/ máquina", type: "number" },
      { name: "noSpeechTimeout", label: "Timeout sem fala (s)", type: "number" },
      { name: "decisionTimeout", label: "Timeout de decisão (s)", type: "number" },
    ],
    summary: d =>
      d.machineTrigger === "stopped" ? "AMD · após saudação" : "AMD · ao detectar máquina",
    // emits a jambonz `config` verb that enables AMD. The actionHook is a
    // placeholder repointed at our backend on deploy; _amd/_amdRoutes/_machineTrigger
    // are internal (stripped before reaching jambonz, kept in definition for runtime).
    build: (d, h) => ({
      verb: "config",
      amd: clean({
        actionHook: "__amd__",
        thresholdWordCount: num(d.thresholdWordCount),
        recognizer: d.language ? { language: d.language } : undefined,
        timers: clean({
          noSpeechTimeoutMs: d.noSpeechTimeout ? num(d.noSpeechTimeout) * 1000 : undefined,
          decisionTimeoutMs: d.decisionTimeout ? num(d.decisionTimeout) * 1000 : undefined,
        }),
      }),
      _amd: true,
      _machineTrigger: d.machineTrigger || "detected",
      _amdRoutes: clean({ human: h.human, machine: h.machine, unknown: h.unknown }),
    }),
  },
  http_request: {
    label: "http_request",
    title: "Enviar HTTP (REST)",
    ai: "Chama uma API externa (GET/POST...). Use para integrar com outro sistema; a resposta fica em steps.<nó>.",
    category: "advanced",
    handles: [{ id: "next", label: "" }],
    defaults: {
      method: "POST",
      url: "",
      headers: "",
      body: '{\n  "opcao": "{{digits}}"\n}',
      saveAs: "",
    },
    fields: [
      { name: "method", label: "Método", type: "select", options: ["GET", "POST", "PUT", "PATCH", "DELETE"] },
      { name: "url", label: "URL", type: "text", required: true },
      { name: "headers", label: "Headers (JSON, opcional)", type: "textarea" },
      // some no GET: o método não carrega corpo e o executor descarta o campo
      {
        name: "body",
        label: "Body (texto/JSON, aceita {{variáveis}})",
        type: "textarea",
        showIf: d => String((d && d.method) || "POST").toUpperCase() !== "GET",
      },
      { name: "saveAs", label: "Salvar resposta em (opcional)", type: "text" },
      {
        name: "timeoutSec",
        label: "Tempo limite (s)",
        type: "number",
        min: 1,
        max: 60,
        advanced: true,
        info: "Quanto esperar pela resposta antes de desistir. Padrão 8 segundos; máximo 60. Cuidado se este nó roda DURANTE uma chamada: enquanto ele espera, quem está na linha ouve silêncio.",
      },
    ],
    summary: d => `${d.method || "POST"} ${d.url || ""}`,
    // exposed to later nodes as steps.<node>.<key> (e.g. {{steps.<node>.json.id}})
    exports: [
      { key: "status", label: "Status HTTP", sample: 200 },
      { key: "json", label: "Resposta (JSON)", sample: {} },
      { key: "body", label: "Resposta (texto)", sample: "" },
      { key: "ok", label: "Sucesso (2xx)", sample: true },
    ],
    build: d =>
      clean({
        verb: "http_request",
        method: d.method || "POST",
        url: d.url,
        headers: jsonOr(d.headers, undefined),
        body: d.body || undefined,
        saveAs: d.saveAs || undefined,
        timeoutSec: num(d.timeoutSec) || undefined,
      }),
  },
  loop: {
    label: "início",
    title: "Repetir para cada item",
    ai: "Início de um bloco que repete os nós internos para cada item de uma lista (ex.: cada cliente). EXIGE um 'Fim da repetição' fechando o bloco.",
    category: "advanced",
    // cor própria (roxo de controle de fluxo) — destaca o bloco e pareia com o Fim
    color: "#5F3DC4",
    handles: [{ id: "next", label: "" }],
    defaults: { list: "", itemAs: "item" },
    fields: [
      {
        name: "list",
        label: "Lista a percorrer",
        type: "text",
        required: true,
        helperText:
          "Aponte para a lista de um passo anterior — ex.: {{steps.buscar.json}}. Aceita array direto ou objeto como {data:[…]} / {items:[…]} / {results:[…]}.",
      },
      {
        name: "itemAs",
        label: "Nome de cada item",
        type: "text",
        helperText: "Referencie o item nos nós DENTRO do bloco: {{item.telefone}}, {{item.nome}}… (e {{item_number}} = posição). Feche o bloco com um nó “Fim da repetição”.",
      },
    ],
    summary: d => `para cada ${d.itemAs || "item"} • ${d.list || "lista…"}`,
    // o item atual fica disponível como {{<itemAs>.*}} (top-level) nos nós ENTRE
    // este e o "Fim da repetição" — esse corpo roda uma vez por item.
    build: d => clean({ verb: "loop", list: d.list || "", itemAs: (d.itemAs || "item").trim() || "item" }),
  },
  loop_end: {
    label: "fim",
    title: "Fim da repetição",
    ai: "Fecha o bloco aberto por 'Repetir para cada item'. Todo loop precisa de um.",
    category: "advanced",
    color: "#5F3DC4",
    handles: [{ id: "next", label: "" }],
    defaults: {},
    fields: [],
    summary: () => "fecha o bloco — repete por item",
    build: () => ({ verb: "loop_end" }),
  },
  http_response: {
    label: "http_response",
    title: "Resposta HTTP",
    ai: "Responde um webhook quando o gatilho está em 'responder no fluxo' (encerra a requisição). SEMPRE preencha o `body` com um JSON que devolve o RESULTADO do fluxo — NUNCA deixe vazio nem só status 200 sem corpo. Referencie os dados produzidos pelos nós anteriores com {{steps.<step>.<campo>}} (ex.: o retorno do nó de código como {{steps.<step_codigo>.<campo>}}, ids, mensagens) e inclua um indicador de sucesso (ex.: \"ok\": true). Em caso de falha esperada, responda com status de erro (4xx/5xx) e uma mensagem no corpo. O `format` define o Content-Type: use 'json' (padrão) salvo se quem chama exigir outro — 'text', 'xml' ou 'html'; nesses casos escreva o corpo já no formato final. Este costuma ser o ÚLTIMO nó de um endpoint (webhook 'responder no fluxo').",
    category: "advanced",
    handles: [{ id: "next", label: "" }],
    defaults: { status: "200", format: "json", body: '{\n  "ok": true\n}' },
    fields: [
      {
        name: "status",
        label: "Código HTTP",
        type: "select",
        options: [
          { value: "200", label: "200 OK" },
          { value: "201", label: "201 Created" },
          { value: "202", label: "202 Accepted" },
          { value: "204", label: "204 No Content" },
          { value: "400", label: "400 Bad Request" },
          { value: "401", label: "401 Unauthorized" },
          { value: "403", label: "403 Forbidden" },
          { value: "404", label: "404 Not Found" },
          { value: "422", label: "422 Unprocessable" },
          { value: "500", label: "500 Server Error" },
        ],
      },
      {
        name: "format",
        label: "Formato da resposta",
        type: "select",
        default: "json",
        helperText: "Define o Content-Type devolvido a quem chamou o webhook.",
        options: [
          { value: "json", label: "JSON (application/json)" },
          { value: "text", label: "Texto (text/plain)" },
          { value: "xml", label: "XML (application/xml)" },
          { value: "html", label: "HTML (text/html)" },
        ],
      },
      {
        name: "body",
        label: "Corpo",
        helperText: "Aceita variáveis. Em JSON o corpo vai como objeto; nos demais formatos, exatamente como escrito.",
        type: "textarea",
      },
    ],
    summary: d => `responde HTTP ${d.status || "200"}${d.format && d.format !== "json" ? ` (${d.format})` : ""}`,
    build: d => {
      // fallback: nunca deixa o corpo vazio (evita "200 seco"); o ideal é o body
      // trazer os dados do fluxo, mas se vier vazio devolve ao menos um "ok".
      const format = d.format || "json";
      const fallback = format === "json" ? '{\n  "ok": true\n}' : "ok";
      return clean({ verb: "http_response", status: d.status || "200", format, body: d.body || fallback });
    },
  },
  code: {
    label: "code",
    title: "Executar código",
    ai: "Executa JavaScript custom quando nenhum nó pronto resolve (transformar dados, lógica específica). Lê passos via steps.*",
    category: "advanced",
    handles: [{ id: "next", label: "" }],
    defaults: {
      code:
        "// `steps` tem a saída dos nós anteriores (ex.: steps.trigger, steps.minha_req.json)\n" +
        "// bibliotecas liberadas: require('axios'|'dayjs'|'lodash')\n" +
        "// retorne o resultado — ele vira steps.<este nó>\n" +
        "return { ok: true };",
    },
    fields: [{ name: "code", label: "Código Node.js (use steps.* e return)", type: "code" }],
    // output is dynamic (whatever the code returns) — exposed as steps.<nó>
    exportsDynamic: true,
    summary: () => "código Node.js",
    build: d => clean({ verb: "code", code: d.code || "" }),
  },
  sms_send: {
    label: "sms_send",
    title: "Enviar SMS",
    ai: "Envia SMS. Credencial é da PLATAFORMA (não há conector nem conta para escolher) — só informe destino e mensagem. O texto passa por triagem antifraude antes de sair: mensagens que se passam por banco/órgão público, alertam 'compra não reconhecida', pedem senha/OTP/Pix ou prometem prêmio são BLOQUEADAS e o fluxo para. Escreva como comunicação legítima da empresa com o próprio cliente.",
    category: "advanced",
    handles: [{ id: "next", label: "" }],
    defaults: { numbers: "", message: "" },
    fields: [
      {
        name: "numbers",
        label: "Número(s) de destino — DDD + número (o 55 é adicionado automaticamente)",
        type: "text",
        required: true,
        placeholder: "11999990000 ou {{steps.trigger.event.body.telefone}}",
        helperText: "Vários números separados por vírgula.",
      },
      {
        name: "message",
        label: "Mensagem (aceita {{variáveis}})",
        type: "textarea",
        required: true,
        helperText: "Passa por triagem antifraude antes do envio.",
      },
    ],
    summary: d => `SMS → ${d.numbers || ""}`,
    exports: [
      { key: "ok", label: "Enviado", sample: true },
      { key: "count", label: "Quantidade de destinatários", sample: 1 },
      { key: "status", label: "Status HTTP", sample: 200 },
      { key: "json", label: "Resposta (JSON)", sample: {} },
    ],
    build: d => clean({ verb: "sms_send", numbers: d.numbers || "", message: d.message || "" }),
  },
  mysql_query: {
    label: "mysql_query",
    title: "Consulta MySQL",
    ai:
      "Roda um SELECT no MySQL do cliente (via um conector do tipo 'mysql' configurado na conta) e " +
      "expõe as linhas em steps.<nó>.rows (array) e .count. Use ? na query para CADA valor e passe os " +
      "valores em params, na ORDEM, um por linha (aceita {{variáveis}}). NUNCA concatene variáveis na " +
      "query — sempre ? + params (injeção). Somente SELECT. Exige um conector mysql (pegue o id na lista de conectores).",
    category: "advanced",
    handles: [{ id: "next", label: "" }],
    defaults: { connectorId: "", query: "SELECT nome, email FROM clientes WHERE telefone = ? LIMIT 10", params: "", maxRecords: "" },
    fields: [
      { name: "connectorId", label: "Conexão MySQL", type: "connector", connectorType: "mysql", required: true },
      { name: "query", label: "Query (SELECT — use ? para os valores)", type: "textarea" },
      { name: "params", label: "Parâmetros (um por linha, na ordem dos ?; aceita {{variáveis}})", type: "textarea", placeholder: "{{steps.trigger.from}}" },
      { name: "maxRecords", label: "Máximo de registros por execução (até 100)", type: "number", min: 1, max: 100, helperText: "Vazio = 100 (máximo por execução)." },
    ],
    exports: [
      { key: "rows", label: "Linhas retornadas (array)", sample: [] },
      { key: "count", label: "Nº de linhas", sample: 0 },
    ],
    summary: () => "consulta MySQL",
    build: d =>
      clean({
        verb: "mysql_query",
        connectorId: d.connectorId || undefined,
        query: d.query || "",
        params: d.params || "",
        maxRecords: Number(d.maxRecords) > 0 ? Number(d.maxRecords) : undefined,
      }),
  },
  brchat_send: {
    label: "brchat_send",
    title: "Enviar mensagem (BR CHAT)",
    ai: "Envia WhatsApp pela API NÃO OFICIAL (BR CHAT / numera.chat). Use quando o usuário quer WhatsApp NÃO oficial ou já tem um conector BR CHAT. Para WhatsApp OFICIAL, use 'conn.gupshup.enviar_whatsapp' (Gupshup).",
    category: "advanced",
    handles: [{ id: "next", label: "" }],
    defaults: {
      connectorId: "",
      baseUrl: "",
      token: "",
      number: "",
      body: "Olá! Recebemos a sua opção {{digits}}.",
    },
    fields: [
      { name: "connectorId", label: "Conector BR CHAT", type: "connector", connectorType: "brchat" },
      { name: "baseUrl", label: "Base URL (se não usar conector)", type: "text" },
      { name: "token", label: "Token x_token (se não usar conector)", type: "text" },
      { name: "number", label: "Número destino — DDD + número (o 55 é adicionado automaticamente)", type: "text", placeholder: "11999990000 ou {{steps.trigger.event.body.from}}" },
      { name: "body", label: "Mensagem (aceita {{variáveis}})", type: "textarea" },
    ],
    summary: d => `WhatsApp → ${d.number || ""}`,
    exports: [
      { key: "status", label: "Status HTTP", sample: 200 },
      { key: "json", label: "Resposta (JSON)", sample: {} },
      { key: "ok", label: "Sucesso (2xx)", sample: true },
    ],
    build: d =>
      clean({
        verb: "brchat_send",
        connectorId: d.connectorId || undefined,
        baseUrl: d.baseUrl,
        token: d.token,
        number: d.number,
        body: d.body,
      }),
  },
  email_send: {
    label: "email_send",
    title: "Enviar e-mail",
    ai: "Envia um e-mail. SEM conector → envia pela NUMERA (o usuário não configura nada). COM conector 'E-mail (SMTP)' → usa o servidor de e-mail do usuário. Use para notificações, confirmações, resumos. Campos: destinatário, assunto e corpo (texto ou HTML, aceita {{variáveis}}). O remetente é opcional: 'fromName' muda só o nome exibido; enviando pela Numera o 'fromEmail' precisa ser do domínio verificado — para receber respostas em outro endereço use 'replyTo', não troque o remetente.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { connectorId: "", fromName: "", fromEmail: "", replyTo: "", to: "", subject: "", body: "" },
    fields: [
      { name: "connectorId", label: "Enviar por", type: "connector", connectorType: "smtp", emptyLabel: "Numera (padrão — sem configurar)", helperText: "Numera = enviar sem configurar nada. Ou escolha um conector SMTP próprio." },
      { name: "fromName", label: "Nome do remetente", type: "text", placeholder: "Sua Empresa", helperText: "Aparece no lugar do nome. Vazio = padrão da conta." },
      { name: "fromEmail", label: "E-mail do remetente", type: "text", placeholder: "no-reply@numeramail.com.br", helperText: "Enviando pela Numera, só endereços do domínio verificado. Com conector próprio, qualquer um do seu servidor.", advanced: true },
      { name: "replyTo", label: "Responder para", type: "text", placeholder: "contato@suaempresa.com.br", helperText: "Para onde vão as respostas. Use isto para receber no seu e-mail sem trocar o remetente." },
      { name: "to", label: "Para (e-mail do destinatário)", type: "text", required: true, placeholder: "{{steps.trigger.event.body.email}}" },
      { name: "subject", label: "Assunto", type: "text", required: true },
      { name: "body", label: "Mensagem (texto ou HTML, aceita {{variáveis}})", type: "textarea" },
    ],
    summary: d => `e-mail → ${d.to || ""}`,
    exports: [
      { key: "ok", label: "Enviado", sample: true },
      { key: "messageId", label: "ID da mensagem", sample: "<abc@smtp>" },
    ],
    build: d =>
      clean({
        verb: "email_send",
        connectorId: d.connectorId || undefined,
        fromName: d.fromName || undefined,
        fromEmail: d.fromEmail || undefined,
        replyTo: d.replyTo || undefined,
        to: d.to,
        subject: d.subject,
        body: d.body,
      }),
  },
  gcal_freebusy: {
    label: "gcal_freebusy",
    title: "Consultar disponibilidade (Google Agenda)",
    ai: "Verifica se um período está LIVRE na Google Agenda do usuário. Use ANTES de marcar, para confirmar que o horário está vago. Exporta 'free' (true = período todo livre) e 'busy' (intervalos ocupados). Datas aceitam '2026-09-01 15:00' ou RFC3339; sem fuso, usa o do conector. Exige o conector Google Agenda conectado.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { connectorId: "", timeMin: "", timeMax: "", calendarId: "", timeZone: "" },
    fields: [
      { name: "connectorId", label: "Conta", type: "connector", connectorType: "google", required: true, helperText: "Qual conta Google este nó usa." },
      { name: "timeMin", label: "Início do período", type: "text", required: true, placeholder: "01/09/2026 15:00", helperText: "DD/MM/AAAA HH:MM. Também aceita 15h30, AAAA-MM-DD e {{variáveis}}." },
      { name: "timeMax", label: "Fim do período", type: "text", required: true, placeholder: "01/09/2026 16:00", helperText: "Mesmo formato do início." },
      { name: "calendarId", label: "Agenda", type: "gcalCalendar", required: true },
      { name: "timeZone", label: "Fuso horário", type: "text", placeholder: "America/Sao_Paulo", advanced: true },
    ],
    summary: d => `livre? ${d.timeMin || ""}`,
    exports: [
      { key: "free", label: "Período livre", sample: true },
      { key: "busy", label: "Intervalos ocupados", sample: [{ start: "", end: "" }] },
      { key: "count", label: "Qtd. de intervalos ocupados", sample: 0 },
    ],
    build: d =>
      clean({
        verb: "gcal_freebusy",
        connectorId: d.connectorId || undefined,
        timeMin: d.timeMin,
        timeMax: d.timeMax,
        calendarId: d.calendarId || undefined,
        timeZone: d.timeZone || undefined,
      }),
  },
  gcal_find_slots: {
    label: "gcal_find_slots",
    title: "Achar horários livres (Google Agenda)",
    ai: "Devolve os PRÓXIMOS horários livres da agenda, já respeitando horário de trabalho, dias da semana, antecedência mínima e folga entre compromissos. É o nó certo para 'quando você pode?' — use este, não o de disponibilidade, quando NÃO houver um horário específico em mente. Exporta 'slots' (lista), 'first' e 'firstStart'/'firstEnd', prontos para alimentar o nó de criar evento. Exige o conector Google Agenda conectado.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { connectorId: "", durationMinutes: "60", minNoticeHours: "2", days: "14", workStart: "09:00", workEnd: "18:00", weekdays: "1,2,3,4,5", incrementMinutes: "30", bufferMinutes: "0", maxSlots: "5", from: "", calendarId: "", timeZone: "" },
    fields: [
      { name: "connectorId", label: "Conta", type: "connector", connectorType: "google", required: true, helperText: "Qual conta Google este nó usa." },
      { name: "durationMinutes", label: "Duração do compromisso (minutos)", type: "text", required: true, placeholder: "60" },
      { name: "minNoticeHours", label: "Antecedência mínima (horas)", type: "text", placeholder: "2", helperText: "Não oferece horário antes de agora + este tempo. Evita marcar para daqui a 10 minutos." },
      { name: "days", label: "Procurar nos próximos (dias)", type: "text", placeholder: "14" },
      { name: "workStart", label: "Atende a partir de", type: "text", placeholder: "09:00" },
      { name: "workEnd", label: "Atende até", type: "text", placeholder: "18:00" },
      { name: "weekdays", label: "Dias da semana", type: "text", placeholder: "1,2,3,4,5", helperText: "0=domingo … 6=sábado. Ou 'uteis' / 'todos'." },
      { name: "maxSlots", label: "Quantos horários devolver", type: "text", placeholder: "5" },
      { name: "incrementMinutes", label: "Horários começam de (minutos em minutos)", type: "text", placeholder: "30", advanced: true },
      { name: "bufferMinutes", label: "Folga antes e depois de cada compromisso (minutos)", type: "text", placeholder: "0", advanced: true },
      { name: "from", label: "Procurar a partir de", type: "text", placeholder: "Vazio = agora", helperText: "DD/MM/AAAA HH:MM (ou 15h30, AAAA-MM-DD, {{variáveis}}). Vazio = agora.", advanced: true },
      { name: "calendarId", label: "Agenda", type: "gcalCalendar", required: true },
      { name: "timeZone", label: "Fuso horário", type: "text", placeholder: "America/Sao_Paulo", advanced: true },
    ],
    summary: d => `livres · ${d.durationMinutes || 60}min`,
    exports: [
      { key: "slots", label: "Horários livres", sample: [{ start: "", end: "" }] },
      { key: "count", label: "Quantidade", sample: 3 },
      { key: "hasSlots", label: "Achou algum", sample: true },
      { key: "first", label: "Primeiro horário", sample: { start: "", end: "" } },
      { key: "firstStart", label: "Início do primeiro", sample: "" },
      { key: "firstEnd", label: "Fim do primeiro", sample: "" },
    ],
    build: d =>
      clean({
        verb: "gcal_find_slots",
        connectorId: d.connectorId || undefined,
        durationMinutes: d.durationMinutes,
        minNoticeHours: d.minNoticeHours || undefined,
        days: d.days || undefined,
        workStart: d.workStart || undefined,
        workEnd: d.workEnd || undefined,
        weekdays: d.weekdays || undefined,
        maxSlots: d.maxSlots || undefined,
        incrementMinutes: d.incrementMinutes || undefined,
        bufferMinutes: d.bufferMinutes || undefined,
        from: d.from || undefined,
        calendarId: d.calendarId || undefined,
        timeZone: d.timeZone || undefined,
      }),
  },
  gcal_event_create: {
    label: "gcal_event_create",
    title: "Criar evento na Google Agenda",
    ai: "Cria um compromisso na Google Agenda do usuário. Informe título e início; o fim vem do campo 'fim' ou da duração em minutos (padrão 60). Convidados recebem convite por e-mail. Exporta eventId, htmlLink e meetLink. Confirme antes com 'Consultar disponibilidade'. Exige o conector Google Agenda conectado.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { connectorId: "", summary: "", start: "", end: "", durationMinutes: "60", attendees: "", description: "", location: "", calendarId: "", timeZone: "", conference: false, notify: true },
    fields: [
      { name: "connectorId", label: "Conta", type: "connector", connectorType: "google", required: true, helperText: "Qual conta Google este nó usa." },
      { name: "summary", label: "Título do compromisso", type: "text", required: true, placeholder: "Consulta — {{steps.trigger.event.body.nome}}" },
      { name: "start", label: "Início", type: "text", required: true, placeholder: "01/09/2026 15:00", helperText: "DD/MM/AAAA HH:MM. Também aceita 15h30, AAAA-MM-DD e {{variáveis}}." },
      { name: "end", label: "Fim", type: "text", placeholder: "01/09/2026 16:00", helperText: "Mesmo formato do início. Vazio = início + duração." },
      { name: "durationMinutes", label: "Duração (minutos)", type: "text", placeholder: "60", helperText: "Usada só quando o fim está vazio." },
      { name: "attendees", label: "Convidados (e-mails)", type: "text", placeholder: "cliente@email.com, outro@email.com" },
      { name: "description", label: "Descrição", type: "textarea" },
      { name: "location", label: "Local", type: "text", advanced: true },
      { name: "conference", label: "Criar link do Google Meet", type: "boolean", advanced: true },
      { name: "notify", label: "Avisar convidados por e-mail", type: "boolean", advanced: true },
      { name: "calendarId", label: "Agenda", type: "gcalCalendar", required: true },
      { name: "timeZone", label: "Fuso horário", type: "text", placeholder: "America/Sao_Paulo", advanced: true },
    ],
    summary: d => `agenda → ${d.summary || ""}`,
    exports: [
      { key: "eventId", label: "ID do evento", sample: "abc123" },
      { key: "htmlLink", label: "Link do evento", sample: "https://calendar.google.com/…" },
      { key: "meetLink", label: "Link do Meet", sample: null },
      { key: "start", label: "Início", sample: "" },
      { key: "end", label: "Fim", sample: "" },
    ],
    build: d =>
      clean({
        verb: "gcal_event_create",
        connectorId: d.connectorId || undefined,
        summary: d.summary,
        start: d.start,
        end: d.end || undefined,
        durationMinutes: d.durationMinutes || undefined,
        attendees: d.attendees || undefined,
        description: d.description || undefined,
        location: d.location || undefined,
        conference: d.conference ? true : undefined,
        notify: d.notify === false ? false : undefined,
        calendarId: d.calendarId || undefined,
        timeZone: d.timeZone || undefined,
      }),
  },
  gcal_events_list: {
    label: "gcal_events_list",
    title: "Listar eventos da Google Agenda",
    ai: "Lista os compromissos de um período na Google Agenda do usuário. Use para responder 'o que tenho amanhã?' ou para achar o evento que será cancelado. Exporta 'events' (lista) e 'first'. Sem início informado, começa em agora. Exige o conector Google Agenda conectado.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { connectorId: "", timeMin: "", timeMax: "", q: "", maxResults: "25", calendarId: "", timeZone: "" },
    fields: [
      { name: "connectorId", label: "Conta", type: "connector", connectorType: "google", required: true, helperText: "Qual conta Google este nó usa." },
      { name: "timeMin", label: "A partir de", type: "text", placeholder: "Vazio = agora", helperText: "DD/MM/AAAA HH:MM. Também aceita 15h30, AAAA-MM-DD e {{variáveis}}. Vazio = agora." },
      { name: "timeMax", label: "Até", type: "text", placeholder: "02/09/2026 00:00", helperText: "Mesmo formato." },
      { name: "q", label: "Buscar por texto", type: "text", placeholder: "nome do cliente" },
      { name: "maxResults", label: "Máximo de eventos", type: "text", placeholder: "25", advanced: true },
      { name: "calendarId", label: "Agenda", type: "gcalCalendar", required: true },
      { name: "timeZone", label: "Fuso horário", type: "text", placeholder: "America/Sao_Paulo", advanced: true },
    ],
    summary: d => `eventos${d.timeMin ? ` desde ${d.timeMin}` : ""}`,
    exports: [
      { key: "events", label: "Eventos", sample: [{ id: "", summary: "", start: "", end: "" }] },
      { key: "count", label: "Quantidade", sample: 0 },
      { key: "first", label: "Primeiro evento", sample: {} },
    ],
    build: d =>
      clean({
        verb: "gcal_events_list",
        connectorId: d.connectorId || undefined,
        timeMin: d.timeMin || undefined,
        timeMax: d.timeMax || undefined,
        q: d.q || undefined,
        maxResults: d.maxResults || undefined,
        calendarId: d.calendarId || undefined,
        timeZone: d.timeZone || undefined,
      }),
  },
  gcal_event_delete: {
    label: "gcal_event_delete",
    title: "Cancelar evento da Google Agenda",
    ai: "Cancela um compromisso da Google Agenda pelo ID. Pegue o ID em 'Listar eventos' ou no 'eventId' do nó que criou o evento. Os convidados são avisados por e-mail. Exige o conector Google Agenda conectado.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { connectorId: "", eventId: "", calendarId: "", notify: true },
    fields: [
      { name: "connectorId", label: "Conta", type: "connector", connectorType: "google", required: true, helperText: "Qual conta Google este nó usa." },
      { name: "eventId", label: "ID do evento", type: "text", required: true, placeholder: "{{steps.listar.first.id}}" },
      { name: "notify", label: "Avisar convidados por e-mail", type: "boolean", advanced: true },
      { name: "calendarId", label: "Agenda", type: "gcalCalendar", required: true },
    ],
    summary: d => `cancelar → ${d.eventId || ""}`,
    exports: [
      { key: "ok", label: "Cancelado", sample: true },
      { key: "eventId", label: "ID do evento", sample: "abc123" },
    ],
    build: d =>
      clean({
        verb: "gcal_event_delete",
        connectorId: d.connectorId || undefined,
        eventId: d.eventId,
        notify: d.notify === false ? false : undefined,
        calendarId: d.calendarId || undefined,
      }),
  },
  context_set: {
    label: "context_set",
    title: "Guardar no contexto",
    ai: "Guarda um valor numa variável do fluxo para usar em passos seguintes.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { key: "", value: "" },
    fields: [
      { name: "key", label: "Chave (ex.: cnpj)", type: "text", required: true },
      { name: "value", label: "Valor (aceita {{variáveis}})", type: "text" },
    ],
    // memória de trabalho da CHAMADA: lida em qualquer nó/ferramenta como
    // {{session.<chave>}}. Serve para autenticação (gravar o cliente) e para a
    // transferência "quente" entre agentes (carrega identidade + dados).
    summary: d => `session.${d.key || ""}`,
    build: d => clean({ verb: "context_set", key: d.key, value: d.value }),
  },
  data_set: {
    label: "data_set",
    title: "Salvar dado",
    ai: "Grava um dado persistente (chave/valor) que sobrevive entre execuções.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { collection: "default", key: "", value: "" },
    fields: [
      { name: "collection", label: "Coleção (ex.: clientes)", type: "text" },
      { name: "key", label: "Chave (ex.: {{steps.trigger.event.body.telefone}})", type: "text" },
      { name: "value", label: "Valor (texto ou JSON, aceita {{variáveis}})", type: "textarea" },
    ],
    summary: d => `${d.collection || "default"}/${d.key || ""}`,
    build: d =>
      clean({ verb: "data_set", collection: d.collection || "default", key: d.key, value: d.value }),
  },
  data_get: {
    label: "data_get",
    title: "Buscar dado",
    ai: "Lê um dado persistente salvo antes (chave). O valor fica em steps.<nó>.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { collection: "default", key: "", saveAs: "dado" },
    fields: [
      { name: "collection", label: "Coleção (ex.: clientes)", type: "text" },
      { name: "key", label: "Chave (ex.: {{steps.trigger.event.body.telefone}})", type: "text" },
      { name: "saveAs", label: "Salvar resultado em (ex.: cliente → use {{cliente.nome}})", type: "text" },
    ],
    summary: d => `${d.collection || "default"}/${d.key || ""}`,
    exports: [
      { key: "value", label: "Valor encontrado", sample: "" },
      { key: "found", label: "Encontrou?", sample: true },
    ],
    build: d =>
      clean({ verb: "data_get", collection: d.collection || "default", key: d.key, saveAs: d.saveAs || "dado" }),
  },
  record_save: {
    label: "record_save",
    title: "Salvar registro",
    ai: "Acrescenta uma LINHA a uma Tabela (para relatórios). Diferente de 'Salvar dado' (chave/valor que sobrescreve): aqui CADA gravação vira um registro novo, com esquema. Use dentro de 'Repetir para cada item' para registrar cada item. runId e applicationId são carimbados sozinhos.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { datasetId: "", values: {} },
    fields: [
      { name: "datasetId", label: "Tabela", type: "dataset", required: true },
      { name: "values", label: "Campos do registro", type: "recordMapping" },
    ],
    summary: d => (d.datasetId ? "1 registro" : "sem tabela"),
    build: d => clean({ verb: "record_save", datasetId: d.datasetId || undefined, values: d.values || {} }),
  },
  record_query: {
    label: "record_query",
    title: "Buscar registros",
    ai: "LÊ linhas de uma Tabela da conta com filtros (campo, operador =|≠|contém|>|<|vazio|notempty, valor — aceita {{variáveis}}). Use quando o fluxo precisa CONSULTAR uma lista que o cliente mantém (vagas por cidade, preços, unidades, horários, clientes…). Saída: registros (lista; cada item tem os campos da tabela + id), total, primeiro (o 1º registro), encontrou (true/false). Ex.: filters:[{campo:'cidade', op:'=', valor:'{{steps.code.cidade}}'}] e depois um branch em {{steps.record_query.encontrou}}.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { datasetId: "", filters: [], limit: 20 },
    fields: [
      { name: "datasetId", label: "Tabela", type: "dataset", required: true },
      { name: "filters", label: "Filtros", type: "recordFilters" },
      { name: "limit", label: "Máximo de registros", type: "number" },
    ],
    exports: [
      { key: "registros", label: "Registros encontrados", sample: [] },
      { key: "total", label: "Total", sample: 0 },
      { key: "primeiro", label: "Primeiro registro", sample: {} },
      { key: "encontrou", label: "Encontrou?", sample: false },
    ],
    summary: d => (d.datasetId ? `${(d.filters || []).length} filtro(s)` : "sem tabela"),
    build: d => clean({ verb: "record_query", datasetId: d.datasetId || undefined, filters: Array.isArray(d.filters) ? d.filters : [], limit: d.limit ? Number(d.limit) : undefined }),
  },
  code_job_run: {
    label: "code_job_run",
    title: "Rodar script",
    ai:
      "Chama um SCRIPT da conta (um script Node.js publicado pela IA do usuário via MCP, ferramenta deploy_job) e espera o resultado. " +
      "Use quando a lógica é de INTEGRAÇÃO/regra pesada que já existe como script (consultar uma API com paginação, aplicar regra, montar relatório) — em vez de dezenas de nós http/code. " +
      "jobId = id do script (list_jobs); input = objeto JSON que o script recebe em studio.entrada (aceita {{variáveis}}); dryRun = modo de teste. " +
      "Saída: ok, resultado (o return do script), erro, log (linhas do console) e duracaoMs. Para rodar no HORÁRIO, ponha este nó num fluxo com gatilho scheduled. A rodada pode levar até 5 min.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { jobId: "", input: "{}", dryRun: false },
    fields: [
      { name: "jobId", label: "Script", type: "codeJob", required: true },
      {
        name: "input",
        label: "Entrada (JSON) — o script recebe em studio.entrada; aceita {{variáveis}}",
        type: "textarea",
        placeholder: '{ "numero": "{{steps.trigger.event.from}}" }',
      },
      { name: "dryRun", label: "Modo de teste (studio.dryRun = true: o script só registra o que faria)", type: "boolean" },
    ],
    exports: [
      { key: "ok", label: "Deu certo?", sample: true },
      { key: "resultado", label: "Resultado (return do script)", sample: {} },
      { key: "erro", label: "Erro", sample: "" },
      { key: "log", label: "Log da rodada", sample: [] },
      { key: "duracaoMs", label: "Duração (ms)", sample: 0 },
    ],
    summary: d => (d.jobId ? (d.dryRun ? "modo de teste" : "") : "sem script"),
    build: d => {
      let input = d.input;
      if (typeof input === "string") {
        try {
          input = input.trim() ? JSON.parse(input) : {};
        } catch (e) {
          // JSON inválido fica como texto; o executor recusa com mensagem clara
        }
      }
      return clean({ verb: "code_job_run", jobId: d.jobId ? Number(d.jobId) : undefined, input, dryRun: d.dryRun === true ? true : undefined });
    },
  },
  read_pdf: {
    label: "read_pdf",
    title: "Ler PDF",
    ai: "Extrai o TEXTO de um PDF (sem IA) para os próximos nós usarem — ex.: o CV que o agente de WhatsApp recebeu (link interno /storage?file=<id> do resultado). Aceita também o id do arquivo ou uma URL pública de PDF. Saída: texto, paginas, nome, truncado, semTexto (PDF escaneado vem vazio). Para PERGUNTAR algo sobre o conteúdo, mande {{steps.<este nó>.texto}} a um 'Prompt de IA'.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { file: "" },
    fields: [
      { name: "file", label: "Arquivo PDF (link do Armazenamento, id ou URL pública) — aceita {{variáveis}}", type: "text", required: true, placeholder: "{{steps.transfer_to_ai.result.cv_url}}" },
    ],
    exports: [
      { key: "texto", label: "Texto do PDF", sample: "" },
      { key: "paginas", label: "Páginas", sample: 1 },
      { key: "nome", label: "Nome do arquivo", sample: "" },
      { key: "truncado", label: "Texto cortado?", sample: false },
      { key: "semTexto", label: "Sem texto legível?", sample: false },
    ],
    summary: () => "",
    build: d => clean({ verb: "read_pdf", file: d.file || "" }),
  },
  public_link: {
    label: "public_link",
    title: "Tornar link público",
    ai: "Gera um LINK PÚBLICO (abre sem login) para um arquivo do Armazenamento da conta — ex.: o CV que o agente de WhatsApp recebeu (o agente registra o link interno /storage?file=<id> no resultado). Use quando o arquivo precisa ir para alguém de FORA da conta (e-mail, mensagem). Validade: 1h, 24h, 7d, 30d ou never (sem prazo; dá para revogar no Armazenamento). Saída: url, expiraEm, nome.",
    category: "data",
    handles: [{ id: "next", label: "" }],
    defaults: { file: "", validity: "24h" },
    fields: [
      { name: "file", label: "Arquivo (link interno do Armazenamento) — aceita {{variáveis}}", type: "text", required: true, placeholder: "{{steps.transfer_to_ai.result.cv_url}}" },
      {
        name: "validity",
        label: "Validade",
        type: "select",
        options: [
          { value: "1h", label: "1 hora" },
          { value: "24h", label: "24 horas" },
          { value: "7d", label: "7 dias" },
          { value: "30d", label: "30 dias" },
          { value: "never", label: "Sem prazo (até revogar no Armazenamento)" },
        ],
      },
    ],
    exports: [
      { key: "url", label: "Link público", sample: "" },
      { key: "expiraEm", label: "Expira em", sample: "" },
      { key: "nome", label: "Nome do arquivo", sample: "" },
    ],
    summary: d => ({ "1h": "1 hora", "24h": "24 horas", "7d": "7 dias", "30d": "30 dias", never: "sem prazo" }[d.validity || "24h"] || ""),
    build: d => clean({ verb: "public_link", file: d.file || "", validity: d.validity || "24h" }),
  },
  answer: {
    label: "answer",
    title: "Atender",
    category: "call",
    handles: [{ id: "next", label: "" }],
    defaults: {},
    fields: [],
    summary: () => "",
    build: () => ({ verb: "answer" }),
  },
  hangup: {
    label: "hangup",
    title: "Desligar",
    ai: "Encerra a ligação. Use ao final do atendimento.",
    category: "call",
    terminal: true,
    handles: [],
    defaults: {},
    fields: [],
    summary: () => "",
    build: () => ({ verb: "hangup" }),
  },
  "sip:decline": {
    label: "sip:decline",
    title: "Rejeitar (SIP)",
    category: "call",
    terminal: true,
    handles: [],
    defaults: { status: 486, reason: "Busy Here" },
    fields: [
      { name: "status", label: "Status", type: "number" },
      { name: "reason", label: "Motivo", type: "text" },
    ],
    summary: d => `${d.status}`,
    build: d => clean({ verb: "sip:decline", status: num(d.status), reason: d.reason }),
  },
  "sip:refer": {
    label: "sip:refer",
    title: "Transferência SIP REFER",
    category: "call",
    handles: [{ id: "action", label: "actionHook" }],
    defaults: { referTo: "sip:user@domain.com" },
    fields: [{ name: "referTo", label: "referTo", type: "text" }],
    summary: d => d.referTo,
    build: (d, h) => clean({ verb: "sip:refer", referTo: d.referTo, actionHook: h.action }),
  },

  conference: {
    label: "conference",
    title: "Conferência",
    category: "queue",
    handles: [{ id: "wait", label: "waitHook" }],
    defaults: { name: "sala-1", beep: true, startConferenceOnEnter: true, endConferenceOnExit: false, maxParticipants: 0 },
    fields: [
      { name: "name", label: "Nome da sala", type: "text" },
      { name: "beep", label: "Beep ao entrar", type: "boolean" },
      { name: "startConferenceOnEnter", label: "Iniciar ao entrar", type: "boolean" },
      { name: "endConferenceOnExit", label: "Encerrar ao sair", type: "boolean" },
      { name: "maxParticipants", label: "Máx. participantes", type: "number" },
    ],
    summary: d => d.name,
    build: (d, h) =>
      clean({
        verb: "conference",
        name: d.name,
        beep: d.beep || undefined,
        startConferenceOnEnter: d.startConferenceOnEnter || undefined,
        endConferenceOnExit: d.endConferenceOnExit || undefined,
        maxParticipants: num(d.maxParticipants) || undefined,
        waitHook: h.wait,
      }),
  },
  enqueue: {
    label: "enqueue",
    title: "Entrar na fila",
    category: "queue",
    handles: [
      { id: "action", label: "actionHook" },
      { id: "wait", label: "waitHook" },
    ],
    defaults: { name: "suporte", priority: 1 },
    fields: [
      { name: "name", label: "Nome da fila", type: "text" },
      { name: "priority", label: "Prioridade (0-999)", type: "number" },
    ],
    summary: d => d.name,
    build: (d, h) =>
      clean({ verb: "enqueue", name: d.name, priority: num(d.priority), actionHook: h.action, waitHook: h.wait }),
  },
  dequeue: {
    label: "dequeue",
    title: "Retirar da fila",
    category: "queue",
    handles: [{ id: "action", label: "actionHook" }],
    defaults: { name: "suporte" },
    fields: [{ name: "name", label: "Nome da fila", type: "text" }],
    summary: d => d.name,
    build: (d, h) => clean({ verb: "dequeue", name: d.name, actionHook: h.action }),
  },
  leave: {
    label: "leave",
    title: "Sair (fila/conf.)",
    category: "queue",
    terminal: true,
    handles: [],
    defaults: {},
    fields: [],
    summary: () => "",
    build: () => ({ verb: "leave" }),
  },

  transfer_ai: {
    label: "transfer_ai",
    title: "Transferir para IA",
    ai: "Entrega a ligação a um Agente de IA (voz) que conversa em tempo real com a pessoa. PREFIRA este em vez de say/gather sempre que o fluxo precisar DIALOGAR ao telefone (mais natural). Exige agentId (um agente de voz da conta).",
    category: "ai",
    // tem saída: quando a IA encerra, o fluxo CONTINUA (return-to-flow) com o
    // resultado da IA disponível em steps.<este nó>.* (exportsDynamic).
    handles: [{ id: "next", label: "" }],
    exportsDynamic: true,
    defaults: { agentId: "", inputs: {} },
    fields: [
      { name: "agentId", label: "Agente de IA que vai assumir", type: "agent-select", required: true },
      { name: "inputs", label: "Dados para a IA", type: "agentInputs" },
    ],
    summary: () => "",
    build: d => clean({ verb: "transfer_ai", agentId: d.agentId ? Number(d.agentId) : undefined, inputs: d.inputs && Object.keys(d.inputs).length ? d.inputs : undefined }),
  },
  agent_navigate: {
    label: "agent_navigate",
    title: "Navegar com agente (IA)",
    ai: "Um turno de um AGENTE de IA navegando uma conversa de texto (ex.: um bot de WhatsApp). A CADA mensagem recebida do bot, o agente lê, decide e você usa 'reply' pra responder; quando ele conclui a missão, 'done' vira true e 'result' traz as Saídas do agente (para exportar). O histórico da conversa é mantido por 'conversa' (conversationKey). Use dentro do fluxo 'Ao receber mensagem'.",
    category: "ai",
    handles: [{ id: "next", label: "" }],
    defaults: { agentId: "", mission: "", conversationKey: "{{steps.trigger.event.body.from}}", inbound: "{{steps.trigger.event.body.text}}" },
    fields: [
      { name: "agentId", label: "Agente navegador", type: "agent-select", required: true },
      { name: "mission", label: "Missão desta sessão (o que conseguir com o bot) — aceita {{variáveis}}", type: "textarea", required: true },
      { name: "conversationKey", label: "Chave da conversa (mantém o histórico) — ex.: o contato", type: "text", helperText: "Padrão: o número de quem enviou. Mesmo valor = mesma conversa." },
      { name: "inbound", label: "Mensagem recebida do bot (aceita {{variáveis}})", type: "text" },
    ],
    exports: [
      { key: "reply", label: "Resposta do agente (envie ao bot)", sample: "" },
      { key: "done", label: "Concluiu a missão?", sample: false },
      { key: "result", label: "Resultado (Saídas do agente)", sample: {} },
    ],
    summary: () => "",
    build: d =>
      clean({
        verb: "agent_navigate",
        agentId: d.agentId ? Number(d.agentId) : undefined,
        mission: d.mission || "",
        conversationKey: d.conversationKey || "{{steps.trigger.event.body.from}}",
        inbound: d.inbound || "{{steps.trigger.event.body.text}}",
      }),
  },
  transfer_to_ai: {
    label: "transfer_to_ai",
    title: "Transferir para a IA",
    ai: "Entrega a conversa de WhatsApp a um Agente de IA, que passa a responder as próximas mensagens até concluir a missão. Enquanto a IA conduz, o fluxo NÃO dispara a cada mensagem (a IA 'segura o canal'). Ao concluir, o fluxo CONTINUA por 'next' com o resultado em steps.<este nó>.result / .reply / .done. Use dentro do fluxo 'Ao receber mensagem' do WhatsApp (QRCode). É o espelho do 'Transferir para humano', mas para a IA.",
    category: "ai",
    isTransferAi: true,
    // a saída "next" é a RETOMADA: roda quando a IA conclui. Sem conectar, o
    // atendimento pela IA é terminal (encerra sem seguir o fluxo).
    handles: [{ id: "next", label: "" }],
    defaults: { agentId: "", mission: "", to: "{{steps.trigger.event.body.from}}", exitWords: "", connectorId: "", sessionMinutes: "" },
    fields: [
      { name: "agentId", label: "Agente de IA que vai assumir", type: "agent-select", required: true },
      { name: "mission", label: "Missão (o que a IA deve conseguir) — aceita {{variáveis}}", type: "textarea", required: true },
      { name: "to", label: "Número da conversa (quem a IA vai atender) — aceita {{variáveis}}", type: "text", helperText: "Em 'Ao receber mensagem', deixe o padrão. Se VOCÊ inicia (envio manual), use o número de destino — o mesmo do nó de envio." },
      { name: "exitWords", label: "Palavras de saída (opcional)", type: "text", placeholder: "parar, sair, cancelar", helperText: "Se o contato mandar uma destas, a IA solta a conversa e o fluxo segue. A sessão também encerra por inatividade ou quando a IA conclui." },
      { name: "sessionMinutes", label: "Tempo de sessão sem resposta (minutos)", type: "text", placeholder: "5", helperText: "Quanto tempo a IA segura a conversa sem nova mensagem antes de soltar. Vazio = padrão (5 min). Mín. 1, máx. 120. Falar com pessoas pede mais tempo que falar com robôs." },
      { name: "connectorId", label: "Conexão WhatsApp (QRCode) — vazio = a do gatilho", type: "connector", connectorType: "whatsapp_qr" },
    ],
    exports: [
      { key: "result", label: "Resultado (Saídas do agente)", sample: {} },
      { key: "reply", label: "Última resposta da IA", sample: "" },
      { key: "done", label: "Concluiu?", sample: true },
      { key: "conversationId", label: "ID da conversa (linha + contato) — p/ consultar depois", sample: "" },
      { key: "conversationKey", label: "Contato (número)", sample: "" },
    ],
    summary: () => "",
    // compilado especialmente (ver compile.js: compileTransferAi) — a saída "next"
    // vira o handler de RETOMADA. Este build é fallback (não usado no fluxo normal).
    build: d => clean({ verb: "ai_takeover", agentId: d.agentId ? Number(d.agentId) : undefined, mission: d.mission || "", to: d.to || "{{steps.trigger.event.body.from}}", exitWords: d.exitWords || undefined, connectorId: d.connectorId || undefined, sessionMinutes: d.sessionMinutes || undefined }),
  },
  start_ai_conversation: {
    label: "start_ai_conversation",
    title: "Iniciar conversa com agente de IA",
    ai: "Faz um CONTATO ATIVO por WhatsApp: o Agente de IA ABRE a conversa com o número de destino, enviando a mensagem de abertura, e passa a conduzir as respostas. A abertura aparece como a 1ª fala do agente (vai pro Numera Atende). Como o número é vinculado ao agente (aba Canais), mesmo que a sessão esfrie a resposta do cliente volta pro agente. Ao concluir, o fluxo CONTINUA por 'next'. Use para campanhas/disparos ativos — substitui o 'Enviar mensagem' + 'Transferir para a IA'.",
    category: "ai",
    isStartAi: true,
    // "next" = RETOMADA (roda quando a IA conclui). Sem conectar, é terminal.
    handles: [{ id: "next", label: "" }],
    defaults: { agentId: "", to: "", message: "", mission: "", exitWords: "", connectorId: "", sessionMinutes: "", inputs: {} },
    fields: [
      { name: "agentId", label: "Agente de IA que vai conversar", type: "agent-select", required: true },
      { name: "inputs", label: "Dados para a IA", type: "agentInputs" },
      { name: "to", label: "Número de destino (quem a IA vai chamar) — aceita {{variáveis}}", type: "text", required: true, helperText: "O número do cliente que vai receber a abertura. Pode vir de uma variável do fluxo." },
      { name: "message", label: "Mensagem de abertura — aceita {{variáveis}}", type: "openingMessages", required: true, helperText: "A abertura que o cliente recebe. Pule uma LINHA EM BRANCO para enviar vários balões, um a um com pausa. Se o cliente responder no meio, os balões restantes não são enviados." },
      { name: "mission", label: "Contexto/missão (opcional) — o que a IA deve conseguir", type: "textarea", helperText: "Instrução por conversa (ex.: 'confirmar a consulta de amanhã')." },
      { name: "exitWords", label: "Palavras de saída (opcional)", type: "text", placeholder: "parar, sair, cancelar" },
      { name: "sessionMinutes", label: "Tempo de sessão sem resposta (minutos)", type: "text", placeholder: "5", helperText: "Quanto tempo a IA segura a conversa sem nova mensagem antes de soltar. Vazio = 5 min. Mín. 1, máx. 120." },
      { name: "connectorId", label: "Conexão WhatsApp — vazio = a vinculada ao agente", type: "connector", connectorType: "whatsapp_qr" },
    ],
    exports: [
      { key: "result", label: "Resultado (Saídas do agente)", sample: {} },
      { key: "reply", label: "Última resposta da IA", sample: "" },
      { key: "done", label: "Concluiu?", sample: true },
      { key: "conversationId", label: "ID da conversa (linha + contato) — p/ consultar depois", sample: "" },
      { key: "conversationKey", label: "Contato (número)", sample: "" },
    ],
    summary: () => "",
    // compilado especialmente (compile.js: compileStartAi); build é fallback.
    build: d => clean({ verb: "ai_start", agentId: d.agentId ? Number(d.agentId) : undefined, inputs: d.inputs && Object.keys(d.inputs).length ? d.inputs : undefined, to: d.to || "", message: d.message || "", mission: d.mission || undefined, exitWords: d.exitWords || undefined, connectorId: d.connectorId || undefined, sessionMinutes: d.sessionMinutes || undefined }),
  },
  route_agent: {
    label: "route_agent",
    title: "Rotear para agente",
    category: "ai",
    terminal: true,
    handles: [],
    defaults: { field: "", routes: [], defaultAgentId: "" },
    fields: [
      { name: "field", label: "Rotear por (ex.: {{steps.recepcionista.setor}} ou {{session.setor}})", type: "text" },
      { name: "routes", label: "Rotas (valor → agente)", type: "route-agents" },
      { name: "defaultAgentId", label: "Agente padrão (se nada casar)", type: "agent-select" },
    ],
    // multi-agente: avalia o valor e entrega a ligação ao agente destino na mesma
    // chamada (warm transfer — o contexto da sessão segue pelo callSid).
    summary: d => `rotear por ${d.field || ""}`,
    build: d =>
      clean({
        verb: "route_agent",
        field: d.field,
        routes: (d.routes || [])
          .filter(r => r && r.value && r.agentId)
          .map(r => ({ value: r.value, agentId: Number(r.agentId) })),
        defaultAgentId: d.defaultAgentId ? Number(d.defaultAgentId) : undefined,
      }),
  },
  llm: {
    label: "llm",
    title: "Agente de IA (llm)",
    category: "ai",
    handles: [{ id: "action", label: "llm-done" }],
    defaults: {
      vendor: "openai",
      model: "gpt-4o-realtime-preview-2024-10-01",
      apiKey: "",
      instructions: "Você é uma recepcionista virtual. Fale de forma simpática em português brasileiro.",
      voice: "alloy",
    },
    fields: [
      { name: "vendor", label: "Fornecedor", type: "select", options: ["openai"] },
      { name: "model", label: "Modelo", type: "text" },
      { name: "apiKey", label: "API key", type: "text" },
      { name: "instructions", label: "Instruções", type: "textarea" },
      { name: "voice", label: "Voz", type: "text" },
    ],
    summary: d => d.instructions,
    build: (d, h) =>
      clean({
        verb: "llm",
        vendor: d.vendor || "openai",
        model: d.model,
        auth: d.apiKey ? { apiKey: d.apiKey } : undefined,
        actionHook: h.action,
        llmOptions: {
          response_create: { modalities: ["text", "audio"], instructions: d.instructions, voice: d.voice || "alloy" },
          session_update: { turn_detection: { type: "server_vad", threshold: 0.8, silence_duration_ms: 600 } },
        },
      }),
  },
  ai_prompt: {
    label: "ai_prompt",
    title: "Prompt de IA",
    category: "ai",
    handles: [{ id: "next", label: "" }],
    defaults: { provider: "openai", model: "", system: "", prompt: "" },
    fields: [
      { name: "provider", label: "Fornecedor", type: "select", options: [
        { value: "openai", label: "OpenAI" },
        { value: "anthropic", label: "Anthropic" },
        { value: "gemini", label: "Google Gemini" },
      ] },
      { name: "model", label: "Modelo (opcional)", type: "text", placeholder: "gpt-4o-mini / claude-3-5-sonnet-latest / gemini-1.5-flash" },
      { name: "system", label: "Instruções (system, opcional)", type: "textarea" },
      { name: "prompt", label: "Prompt", type: "textarea" },
    ],
    summary: d => `IA: ${d.provider || "openai"}`,
    exports: [{ key: "text", label: "Resposta", sample: "" }],
    build: d => clean({ verb: "ai_prompt", provider: d.provider || "openai", model: d.model || undefined, system: d.system || undefined, prompt: d.prompt }),
  },
  ai_read_image: {
    label: "ai_read_image",
    title: "Ler imagem (IA)",
    category: "ai",
    handles: [{ id: "next", label: "" }],
    defaults: { provider: "openai", model: "", imageUrl: "", prompt: "Descreva o conteúdo da imagem." },
    fields: [
      { name: "provider", label: "Fornecedor", type: "select", options: [
        { value: "openai", label: "OpenAI" },
        { value: "anthropic", label: "Anthropic" },
        { value: "gemini", label: "Google Gemini" },
      ] },
      { name: "model", label: "Modelo (opcional)", type: "text", placeholder: "gpt-4o / claude-3-5-sonnet-latest / gemini-1.5-flash" },
      { name: "imageUrl", label: "URL da imagem", type: "text", placeholder: "{{steps.trigger.event.body.imageUrl}}" },
      { name: "prompt", label: "Pergunta / instrução", type: "textarea" },
    ],
    summary: () => "Ler imagem (IA)",
    exports: [{ key: "text", label: "Resposta", sample: "" }],
    build: d => clean({ verb: "ai_read_image", provider: d.provider || "openai", model: d.model || undefined, imageUrl: d.imageUrl, prompt: d.prompt || undefined }),
  },
  ai_read_pdf: {
    label: "ai_read_pdf",
    title: "Ler PDF (IA)",
    category: "ai",
    handles: [{ id: "next", label: "" }],
    defaults: { provider: "openai", model: "", pdfUrl: "", prompt: "Resuma o documento." },
    fields: [
      { name: "provider", label: "Fornecedor", type: "select", options: [
        { value: "openai", label: "OpenAI" },
        { value: "anthropic", label: "Anthropic" },
        { value: "gemini", label: "Google Gemini" },
      ] },
      { name: "model", label: "Modelo (opcional)", type: "text", placeholder: "gpt-4o-mini / claude-3-5-sonnet-latest / gemini-1.5-flash" },
      { name: "pdfUrl", label: "URL do PDF", type: "text", placeholder: "{{steps.trigger.event.body.pdfUrl}}" },
      { name: "prompt", label: "Pergunta / instrução", type: "textarea" },
    ],
    summary: () => "Ler PDF (IA)",
    exports: [{ key: "text", label: "Resposta", sample: "" }],
    build: d => clean({ verb: "ai_read_pdf", provider: d.provider || "openai", model: d.model || undefined, pdfUrl: d.pdfUrl, prompt: d.prompt || undefined }),
  },
  transcribe: {
    label: "transcribe",
    title: "Transcrição",
    category: "ai",
    handles: [
      { id: "next", label: "" },
      { id: "transcription", label: "transcriptionHook" },
    ],
    defaults: {},
    fields: [],
    summary: () => "",
    build: (d, h) => clean({ verb: "transcribe", transcriptionHook: h.transcription }),
  },
  listen: {
    label: "listen",
    title: "Streaming (listen)",
    category: "ai",
    handles: [
      { id: "next", label: "" },
      { id: "action", label: "actionHook" },
    ],
    defaults: { url: "wss://recorder.example.com/calls", mixType: "stereo" },
    fields: [
      { name: "url", label: "URL (wss://)", type: "text" },
      { name: "mixType", label: "Mix", type: "select", options: ["mono", "stereo", "mixed"] },
    ],
    summary: d => d.url,
    build: (d, h) => clean({ verb: "listen", url: d.url, mixType: d.mixType, actionHook: h.action }),
  },

  config: {
    label: "config",
    title: "Configurar sessão",
    category: "advanced",
    handles: [{ id: "next", label: "" }],
    defaults: { bargeIn: true },
    fields: [{ name: "bargeIn", label: "Barge-in", type: "boolean" }],
    summary: d => (d.bargeIn ? "bargeIn on" : "bargeIn off"),
    build: d => ({ verb: "config", bargeIn: { enable: !!d.bargeIn, sticky: true } }),
  },
  tag: {
    label: "tag",
    title: "Metadados (tag)",
    category: "advanced",
    handles: [{ id: "next", label: "" }],
    defaults: { dataJson: '{ "accountId": "12345" }' },
    fields: [{ name: "dataJson", label: "Dados (JSON)", type: "textarea" }],
    summary: () => "tag",
    build: d => {
      let data = {};
      try {
        data = JSON.parse(d.dataJson || "{}");
      } catch (e) {
        data = {};
      }
      return { verb: "tag", data };
    },
  },
  alert: {
    label: "alert",
    title: "Notificação externa",
    category: "advanced",
    handles: [{ id: "next", label: "" }],
    defaults: { url: "https://hooks.example.com/alert" },
    fields: [{ name: "url", label: "URL do webhook", type: "text" }],
    summary: d => d.url,
    build: d => clean({ verb: "alert", alert: { url: d.url } }),
  },
};

// maps a handle role to the verb param it fills (named-handler hooks)
export const HOOK_PARAM = {
  action: "actionHook",
  wait: "waitHook",
  to: "actionHook",
  transcription: "transcriptionHook",
};

// roles whose targets become NAMED handlers (vs. inline branch-case sequences)
// IVR digit outputs ("0".."9", "star"=*, "pound"=#) + "noinput" (timeout) are
// also named handlers, like the branch/schedule/amd outputs.
const IVR_KEYS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "star", "pound", "noinput"];
export const NAMED_ROLES = [
  "action", "wait", "to", "transcription",
  "human", "machine", "unknown", "open", "closed",
  "answered", "noanswer",
  ...IVR_KEYS,
];

// Generate one palette node per connector ACTION capability (data-driven). Each
// compiles to a `connector_action` (domínio backend) executed via the connector.
Object.keys(CONNECTOR_CAPS).forEach(conn => {
  const cname = (connByType(conn) || {}).name || conn;
  CONNECTOR_CAPS[conn]
    .filter(c => c.kind === "action")
    .forEach(c => {
      const key = `conn.${conn}.${c.key}`;
      const method = c.method || "POST";
      const hasForm = Array.isArray(c.form) && c.form.length > 0;
      const pathIsFn = typeof c.path === "function";
      const bodyIsFn = typeof c.body === "function";
      // query: para APIs que passam os dados na querystring (ex.: SMS da BR DID).
      // O executor substitui as variáveis e faz o encoding — por isso não dá para
      // montar isso dentro do `path`, que seria substituído DEPOIS de codificado.
      const resolveQuery = d => (typeof c.query === "function" ? c.query(d) : c.query);
      // caminho/corpo resolvidos a partir dos dados do nó (form ou campos brutos)
      const resolvePath = d => (pathIsFn ? c.path(d) : (d.path || c.path || ""));
      const resolveBody = d => {
        if (bodyIsFn) return c.body(d);
        return d.body || undefined;
      };

      // defaults: só guardamos method/path/body brutos quando NÃO há formulário
      const formDefaults = {};
      if (hasForm) c.form.forEach(ff => { if (ff.default !== undefined) formDefaults[ff.name] = ff.default; });
      const rawDefaults = hasForm
        ? {}
        : { method, path: pathIsFn ? "" : (c.path || ""), body: bodyIsFn ? "" : (c.body || "") };

      // campos visíveis: conector + (form amigável) OU (método/caminho no
      // "Avançado" + corpo quando faz sentido). Método/caminho ficam
      // transparentes (advanced) — o usuário não precisa mexer.
      const fields = [{ name: "connectorId", label: `Conector ${cname}`, type: "connector", connectorType: conn }];
      if (hasForm) {
        c.form.forEach(ff => fields.push({ ...ff }));
      } else {
        fields.push({ name: "method", label: "Método", type: "select", options: ["GET", "POST", "PUT", "PATCH", "DELETE"], advanced: true });
        fields.push({ name: "path", label: "Caminho", helperText: "Endpoint. Aceita variáveis.", type: "text", advanced: true });
        if (method !== "GET" || c.body) {
          fields.push({ name: "body", label: "Dados (JSON)", helperText: "Corpo enviado. Aceita variáveis.", type: "textarea" });
        }
      }

      VERBS[key] = {
        label: key,
        title: `${cname} — ${c.label}`,
        category: "connectors",
        handles: [{ id: "next", label: "" }],
        defaults: { connectorId: "", ...rawDefaults, ...formDefaults },
        fields,
        summary: () => c.label,
        exports: [
          { key: "status", label: "Status HTTP", sample: 200 },
          { key: "json", label: "Resposta (JSON)", sample: {} },
          { key: "ok", label: "Sucesso", sample: true },
        ],
        build: d =>
          clean({
            verb: "connector_action",
            connectorType: conn,
            connectorId: d.connectorId || undefined,
            label: `${cname}: ${c.label}`,
            method: d.method || method,
            path: resolvePath(d),
            query: resolveQuery(d),
            body: resolveBody(d),
          }),
      };
    });
});

// Custom UX for the WhatsApp template node — overrides the raw auto-generated
// connector_action (destino + seletor de template + variáveis + testar).
if (VERBS["conn.gupshup.enviar_template"]) {
  VERBS["conn.gupshup.enviar_template"] = {
    label: "conn.gupshup.enviar_template",
    title: "WhatsApp — Enviar template",
    ai: "Envia template de WhatsApp pela API OFICIAL. WhatsApp oficial. Para WhatsApp NÃO oficial, use 'brchat_send' (BR CHAT).",
    category: "connectors",
    handles: [{ id: "next", label: "" }],
    defaults: {
      connectorId: "",
      destination: "",
      templateId: "",
      templateName: "",
      templateBody: "",
      params: [],
    },
    fields: [
      { name: "connectorId", label: "Conector WhatsApp", type: "connector", connectorType: "gupshup" },
      { name: "destination", label: "Destino (número)", type: "text", placeholder: "{{steps.trigger.event.body.from}}" },
      { name: "tpl", label: "Template", type: "whatsappTemplate" },
    ],
    summary: d => (d.templateName ? `→ ${d.templateName}` : "Enviar template"),
    exports: [
      { key: "status", label: "Status HTTP", sample: 200 },
      { key: "json", label: "Resposta (JSON)", sample: {} },
      { key: "ok", label: "Sucesso (2xx)", sample: true },
    ],
    build: d =>
      clean({
        verb: "connector_action",
        connectorType: "gupshup",
        connectorId: d.connectorId || undefined,
        label: "WhatsApp: Enviar template",
        method: "POST",
        path: "/template/msg",
        body: {
          destination: d.destination || "",
          template: JSON.stringify({ id: d.templateId || "", params: d.params || [] }),
        },
      }),
  };
}

// Custom UX para o "Enviar WhatsApp (texto)" — substitui o connector_action cru
// (JSON na mão) por um formulário amigável: destino + mensagem. Monta o body do
// Gupshup por baixo ({ destination, message:{type:text,text} }).
if (VERBS["conn.gupshup.enviar_whatsapp"]) {
  VERBS["conn.gupshup.enviar_whatsapp"] = {
    label: "conn.gupshup.enviar_whatsapp",
    title: "WhatsApp — Enviar mensagem WhatsApp",
    keywords: "zap wpp whats mensagem texto",
    ai: "Envia WhatsApp pela API OFICIAL do WhatsApp. Use quando o usuário quer WhatsApp OFICIAL ou já tem um conector WhatsApp oficial. Para WhatsApp NÃO oficial, use 'brchat_send' (BR CHAT).",
    category: "connectors",
    handles: [{ id: "next", label: "" }],
    defaults: { connectorId: "", destination: "", text: "" },
    fields: [
      { name: "connectorId", label: "Conector WhatsApp", type: "connector", connectorType: "gupshup", required: true },
      { name: "destination", label: "Destino (número)", type: "text", required: true, placeholder: "{{steps.trigger.event.body.telefone}}" },
      { name: "text", label: "Mensagem", type: "textarea", required: true, placeholder: "Olá {{item.nome}}! ..." },
    ],
    summary: d => d.text || "Enviar mensagem WhatsApp",
    exports: [
      { key: "status", label: "Status HTTP", sample: 200 },
      { key: "json", label: "Resposta (JSON)", sample: {} },
      { key: "ok", label: "Sucesso (2xx)", sample: true },
    ],
    build: d =>
      clean({
        verb: "connector_action",
        connectorType: "gupshup",
        connectorId: d.connectorId || undefined,
        label: "WhatsApp: Enviar mensagem",
        method: "POST",
        path: "/msg",
        body: {
          destination: d.destination || "",
          message: JSON.stringify({ type: "text", text: d.text || "" }),
        },
      }),
  };
}

// Custom UX para o "WhatsApp (QRCode) — Enviar mensagem": mesmo formulário
// amigável do BR CHAT (destino + mensagem), mas compila para o verbo backend
// `whatsapp_qr_send`, que entrega pelo socket Baileys self-hosted (não é HTTP).
if (VERBS["conn.whatsapp_qr.enviar_mensagem"]) {
  VERBS["conn.whatsapp_qr.enviar_mensagem"] = {
    label: "conn.whatsapp_qr.enviar_mensagem",
    title: "WhatsApp (QRCode) — Enviar mensagem WhatsApp",
    keywords: "zap wpp whats mensagem texto",
    ai: "Envia WhatsApp pela conexão NÃO OFICIAL via QR Code (self-hosted). Use quando o usuário pareou um número por QR Code. Para WhatsApp OFICIAL use 'conn.gupshup.enviar_whatsapp'; para BR CHAT use 'brchat_send'.",
    category: "connectors",
    handles: [{ id: "next", label: "" }],
    defaults: { connectorId: "", number: "", body: "" },
    fields: [
      { name: "connectorId", label: "Conexão WhatsApp (QRCode)", type: "connector", connectorType: "whatsapp_qr", required: true },
      { name: "number", label: "Destino — DDD + número (o 55 é adicionado automaticamente)", type: "text", required: true, placeholder: "11999990000 ou {{steps.trigger.event.body.from}}" },
      { name: "body", label: "Mensagem (aceita {{variáveis}})", type: "textarea", required: true },
    ],
    summary: d => `WhatsApp → ${d.number || ""}`,
    exports: [
      { key: "ok", label: "Sucesso", sample: true },
      { key: "id", label: "ID da mensagem", sample: "" },
      { key: "error", label: "Erro (se falhar)", sample: "" },
    ],
    build: d =>
      clean({
        verb: "whatsapp_qr_send",
        connectorId: d.connectorId || undefined,
        label: "WhatsApp (QRCode): Enviar mensagem",
        number: d.number,
        body: d.body,
      }),
  };
}

// Consultar nome/foto de um número pela conexão QR (Baileys). Compila para o
// verbo backend `whatsapp_qr_profile`. OBS: foto/recado dependem da privacidade
// do contato; o NOME só vem se o contato já mandou mensagem (o WhatsApp não
// expõe o nome de exibição de um número qualquer).
if (VERBS["conn.whatsapp_qr.consultar_perfil"]) {
  VERBS["conn.whatsapp_qr.consultar_perfil"] = {
    label: "conn.whatsapp_qr.consultar_perfil",
    title: "WhatsApp (QRCode) — Extrair nome e foto",
    ai: "Consulta se um número está no WhatsApp e devolve foto, recado (status) e, quando for conta comercial, o perfil Business — pela conexão NÃO OFICIAL via QR Code (self-hosted). O NOME só vem se o contato já tiver enviado mensagem (limitação do WhatsApp).",
    category: "connectors",
    handles: [{ id: "next", label: "" }],
    defaults: { connectorId: "", number: "" },
    fields: [
      { name: "connectorId", label: "Conexão WhatsApp (QRCode)", type: "connector", connectorType: "whatsapp_qr", required: true },
      { name: "number", label: "Número — DDD + número (o 55 é adicionado automaticamente)", type: "text", required: true, placeholder: "11999990000 ou {{steps.trigger.event.body.from}}" },
    ],
    summary: d => `Perfil WhatsApp ← ${d.number || ""}`,
    exports: [
      { key: "exists", label: "Está no WhatsApp?", sample: true },
      { key: "name", label: "Nome (se disponível)", sample: "" },
      { key: "photo", label: "Foto (URL)", sample: "" },
      { key: "status", label: "Recado/status", sample: "" },
      { key: "jid", label: "JID", sample: "" },
      { key: "business", label: "Perfil comercial (se Business)", sample: null },
    ],
    build: d =>
      clean({
        verb: "whatsapp_qr_profile",
        connectorId: d.connectorId || undefined,
        label: "WhatsApp (QRCode): Extrair nome e foto",
        number: d.number,
      }),
  };
}

export const verbsByCategory = cat =>
  Object.keys(VERBS)
    .filter(k => VERBS[k].category === cat)
    .map(k => ({ key: k, ...VERBS[k] }));

// Palette grouping (estilo Pipedream): grupo à esquerda, componentes à direita.
// Grupos "core" reúnem várias categorias; cada CONECTOR (marca) vira seu próprio
// grupo, com logo e ações na direita.
const CORE_GROUPS = [
  { key: "telephony", label: "Telefonia", color: "#1E7E34", cats: ["call", "audio", "input", "queue"] },
  { key: "logic", label: "Fluxo & Lógica", color: "#5F3DC4", cats: ["routing", "advanced"] },
  { key: "ai", label: "Inteligência", color: "#A61E4D", cats: ["ai"] },
  { key: "data", label: "Dados", color: "#2B8A3E", cats: ["data"] },
];

// verbos legados que pertencem a uma marca mas não seguem o padrão "conn.<tipo>."
// verbos que não seguem o padrão "conn.<tipo>.<ação>" mas pertencem a uma marca:
// agrupa na paleta sob o conector e herda o ícone da marca.
const VERB_BRAND = {
  brchat_send: "brchat",
  gcal_freebusy: "google",
  gcal_find_slots: "google",
  gcal_event_create: "google",
  gcal_events_list: "google",
  gcal_event_delete: "google",
};

// connectorType de um verbo de conector ("conn.<tipo>.<ação>" ou legado) ou null
export const connectorTypeOf = verbKey => {
  if (VERB_BRAND[verbKey]) return VERB_BRAND[verbKey];
  const m = /^conn\.([^.]+)\./.exec(verbKey || "");
  return m ? m[1] : null;
};

// modo Ferramenta/Coletor: só nós de backend (sem chamada) + o nó terminal do
// modo (Retornar/Retornar ao coletor); modo normal esconde esses terminais
// (toolOnly/collectorOnly). Aplica-se a verbos core e de conector.
// nós que NÃO funcionam numa ferramenta/coletor (rodam no backend, sem ligação e
// sem conversa viva): verbos do jambonz que o motor da ferramenta pula em
// silêncio (config, tag, alert, llm, transcribe, listen) e nós que dependem de
// uma conversa de WhatsApp em andamento (menu que espera resposta, navegar/
// assumir a conversa com IA).
const BACKEND_MODE_HIDDEN = ["config", "tag", "alert", "llm", "transcribe", "listen", "wa_menu", "agent_navigate", "transfer_to_ai"];
const allowVerb = (v, mode) => {
  const tool = mode === "tool";
  const collector = mode === "collector";
  if (v.toolOnly) return tool;
  if (v.collectorOnly) return collector;
  if (tool || collector) {
    if (v.key === "originate" || v.key === "redirect" || v.key === "schedule" || v.key === "http_response") return false;
    if (BACKEND_MODE_HIDDEN.includes(v.key)) return false;
    return !needsCall(v.key);
  }
  return true;
};

export const paletteGroups = (opts = {}) => {
  const mode = opts.tool ? "tool" : opts.collector ? "collector" : null;
  const backend = mode === "tool" || mode === "collector";
  const groups = [];

  CORE_GROUPS.forEach(g => {
    const verbs = g.cats
      .flatMap(c => verbsByCategory(c))
      .filter(v => !connectorTypeOf(v.key) && allowVerb(v, mode));
    if (verbs.length) groups.push({ ...g, kind: "core", verbs });
  });

  // um grupo por marca de conector (qualquer verbo com connectorType, inclusive
  // os legados como brchat_send)
  const byConn = {};
  Object.keys(VERBS).forEach(k => {
    const ct = connectorTypeOf(k);
    if (ct) (byConn[ct] = byConn[ct] || []).push({ key: k, ...VERBS[k] });
  });
  // eventos (kind "event") da marca entram como itens informativos marcados como
  // gatilho — pra ver TODAS as opções da marca num lugar só (não são adicionáveis
  // como nó; são configurados no seletor de gatilho).
  Object.keys(CONNECTOR_CAPS).forEach(ct => {
    if (!byConn[ct]) return; // só marcas que já têm grupo (têm ações)
    CONNECTOR_CAPS[ct]
      .filter(c => c.kind === "event")
      .forEach(c =>
        byConn[ct].push({
          key: `connevt:${ct}:${c.key}`,
          title: c.label,
          category: "connectors",
          isTrigger: true,
        })
      );
  });
  Object.keys(byConn)
    .map(ct => ({ ct, meta: connByType(ct) || {} }))
    .sort((a, b) => (a.meta.name || a.ct).localeCompare(b.meta.name || b.ct))
    .forEach(({ ct, meta }) => {
      // em modo Ferramenta/Coletor, oculta verbos de chamada e eventos/gatilhos do conector
      const verbs = byConn[ct].filter(v => allowVerb(v, mode) && !(backend && v.isTrigger));
      if (!verbs.length) return;
      groups.push({
        key: `conn:${ct}`,
        label: meta.name || ct,
        color: meta.color || "#0B7285",
        kind: "connector",
        connectorType: ct,
        icon: meta.icon,
        verbs,
      });
    });

  return groups;
};

// título "limpo" do componente dentro de um grupo de conector ("Marca — Ação" -> "Ação")
export const shortVerbTitle = (title, groupLabel) => {
  if (!title) return title;
  const dash = title.indexOf("—");
  if (dash >= 0) return title.slice(dash + 1).trim();
  if (groupLabel && title.startsWith(groupLabel)) return title.slice(groupLabel.length).replace(/^[\s—-]+/, "");
  return title;
};

// nodes that only make sense DURING a live call (transferir, áudio, coletar,
// fila, URA…). They require a call context: a call trigger or an "Iniciar
// chamada" node earlier in the flow. Routing/data/connector/AI work without one.
export const CALL_CATEGORIES = ["call", "audio", "input", "queue"];
// …EXCEPT nodes that THEMSELVES create/start a call. These must be usable at any
// moment (they're what provides the call context), so they're never blocked even
// though they live in a CALL_CATEGORY. "originate" = Iniciar chamada.
export const CALL_PROVIDERS = ["originate"];
// verbos que EXIGEM uma chamada viva mas não estão numa CALL_CATEGORY (ex.:
// "Transferir para IA" é categoria "ai" mas só roda durante uma chamada).
export const CALL_CONSUMERS = ["transfer_ai", "route_agent"];
export const needsCall = verbKey => {
  if (verbKey === "wa_menu") return false; // menu de WhatsApp é mensagem, não chamada
  if (CALL_PROVIDERS.indexOf(verbKey) >= 0) return false;
  if (CALL_CONSUMERS.indexOf(verbKey) >= 0) return true;
  const def = VERBS[verbKey];
  return !!(def && CALL_CATEGORIES.indexOf(def.category) >= 0);
};

// Deterministic step name per node (used both to build the {{steps.*}} scope and
// to stamp _name on compiled verbs, so design-time refs match runtime). Same
// elements array in -> same names. Trigger/start nodes are skipped.
export const stepNamesFor = elements => {
  const map = {};
  const counts = {};
  (elements || []).forEach(n => {
    const vk = n && n.data && n.data.verbKey;
    if (!vk || vk === "__trigger__" || vk === "__start__") return;
    counts[vk] = (counts[vk] || 0) + 1;
    // pontos no verbKey (conectores: conn.<tipo>.<ação>) viram "_": o nome do
    // passo precisa ser uma chave SEM ponto, senão {{steps.<passo>.json…}} não
    // resolve (getByPath divide por "."). Só afeta conectores (nenhum outro verbo
    // tem ponto) — por isso é seguro e destrava o encadeamento entre conectores.
    map[n.id] = (counts[vk] > 1 ? `${vk}_${counts[vk]}` : vk).replace(/\./g, "_");
  });
  return map;
};
